// frontend/src/pages/OrganisationalCalendar.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Updated:      2026-10-16 (BST; exact editing time not recorded)
// 2026-10-16 BUILD FIX 2: Corrected mobile unassigned card label to avoid out-of-scope taskListLabel.
// 2026-10-16 BUILD FIX: Removed two invalid task-list labels from Job-only card renderers.
// 2026-10-16 TASK LIST SCHEDULER:
// 2026-10-16: Task List cards show Job ref + title / WP title; preserve exact Monday weekly rail comparison.
// - Project Task Lists as one scheduling unit; preserve standalone task and job paths.
// - Preserve week rail and left backlog split; no member-task duplicates.
//
// 2026-09-04 MOBILE PLANNER:
// - Desktop planner remains unchanged.
// - Added a purpose-built <=768px planner rather than compressing the desktop matrix.
// - Week: compact seven-day selector + vertically stacked resources for the selected day.
// - Month: mobile month grid + vertically stacked resources for the selected day.
// - Day: vertically stacked resources for the selected day.
// - Agenda: chronological assignment cards.
// - Unassigned jobs: tap Assign to open a mobile assignment sheet; no drag/drop required.
// - Mobile assignment sheet uses the existing POST /api/jobs/{job_id}/resources workflow.
// - Mobile Settings uses explicit Move Up / Move Down controls for resource ordering;
//   changes apply and persist immediately; desktop Settings retains drag-and-drop ordering.
// - Native colour inputs now convert deterministic HSL defaults to their real HEX colour
//   rather than displaying the browser's fallback blue.
// - Existing API calls, desktop drag/drop, context menus, assignment mutations, settings,
//   resource order/visibility/colour preferences and localStorage keys are preserved.
//
// ✍️ Change Log (GMT/BST):
// 2026-10-08 12:34 BST WEEK-VIEW UNASSIGNED TOP RAIL:
// - Added a Week-view-only horizontal rail between calendar controls and the resource planner.
// - Unassigned jobs/tasks whose canonical week_commencing matches the visible Monday move to this rail.
// - Existing left Unassigned Work rail remains and now holds all other unassigned work while in Week view.
// - Changing visible week repartitions the two pools immediately; existing drag/drop assignment paths are unchanged.
// - Preserved week_commencing on the flat-job unassigned projection so WC-only jobs can be partitioned correctly.
//
// 2026-10-07 14:18 BST WORKFLOW TASK PLANNER INTEGRATION:
// - Added workflow task planner projection alongside the existing flat-job planner feed.
// - Existing flat-job assignment APIs and drag/drop branches remain unchanged.
// - Workflow tasks use canonical task assignment create/update/deactivate endpoints.
// - Unassigned workflow tasks can be dragged onto a resource/day or resource/time slot.
// - Assigned workflow tasks can be moved/reassigned without mutating the published template.
// - Task availability conflicts support an explicit authorised override with mandatory reason.
// - awaiting_reassignment tasks are surfaced as attention items in the unassigned pool.
// - Runtime task assignment state is refreshed from /api/planner/work-items.
//
// 12:06  Added right-click context menu on events (Matrix + RBC): Unassign / Reassign.
// 12:06  Unassign = nested DELETE /api/jobs/{job_id}/resources/{assignment_id}. Reassign = PATCH /api/job-assignments/{id}.
// 12:06  UI refresh + strict unassigned list.
// 15:52  Real toasts only. Collapsible left-rail panels with persistence.
// 15:52  Robust unassigned fetch (strict: only jobs with no active resource assignments).
// 17:45  Per-resource visibility (Week matrix) + persistence (LS_RESOURCE_VISIBILITY_KEY).
// 17:45  Settings modal shows ALL resources; drag-sort there + Week header.
// 17:45  Single default export (no placeholders).
// 18:22  FIX: visibility key typo; show/hide now works.
// 18:22  FIX: colour picker now updates parent state + localStorage instantly (persists).
// 18:22  IMPROVE: visibility toggle also applies instantly (no need to Save).
// 21:12  FIX: Day/Month/Agenda buttons → explicit view setter + resize tick; RBC given explicit `views`.
// 21:18  FIX: Header drag reorder respects visibility (reorders visible subset and merges back).
// 12:24  ✅ FIX (view buttons): unified setCalendarView() for toolbar + RBC + sidebar.
// 12:40  🎨 STYLE: View + Today/Prev/Next use BLUE general palette; Settings uses ORANGE action palette.
// 09:45  🎨 THEME: General/nav → bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white;
//       Action → bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white.
// 10:06  🎨 THEME: Inactive Day / This Week / Months chips → transparent bg + 1px accent border + accent text.
// 10:28  🛠️ FIX: Removed stray token in moveAssignmentToMatrixCell that broke eslint/build.
// 16:35  👥 NEW: Co-Resource support — duplicate pills for co_resource_id, full drag/resize + context menu.
// 16:45  ✅ FIX: Calendar never shows redacted/inactive resources.
// 16:28  ✅ CHANGE: Week + Month now use resource-first planner matrix.
// 16:28  ✅ CHANGE: Week/Month stack assignments at next available hour for resource/day.
// 16:28  ✅ CHANGE: Day view remains the time-grid with hours down the left.
// 16:34  ✅ FIX: Mini month now renders correct weekday alignment using leading/trailing blanks.
// 16:38  ✅ FIX: Agenda now renders via plain RBC Calendar instead of DnD/resource mode.
//

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Calendar, Views, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  startOfMonth,
  startOfWeek,
  getDay,
  setHours,
  setMinutes,
} from "date-fns";
import enGB from "date-fns/locale/en-GB";

import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext";
import {
  FaChevronLeft,
  FaChevronRight,
  FaCog,
  FaChevronDown,
  FaChevronRight as FaRight,
} from "react-icons/fa";

/* ------------------------------ Setup ------------------------------ */

const DnDCalendar = withDragAndDrop(Calendar);
const locales = { "en-GB": enGB };
const localizer = dateFnsLocalizer({
  format,
  parse: (str) => new Date(str),
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const weekdayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const LS_RESOURCE_ORDER_KEY = "resource_order_v1";
const LS_RESOURCE_COLOURS_KEY = "resource_colours_v1";
const LS_RESOURCE_VISIBILITY_KEY = "resource_visibility_v1";
const LS_CAL_SETTINGS_KEY = "calendar_settings_v1";
const LS_COLLAPSE_WEEK_KEY = "cal_collapse_week_v1";
const LS_COLLAPSE_MONTHS_KEY = "cal_collapse_months_v1";

const DEFAULT_SETTINGS = { startHour: 6, endHour: 18, slotMode: "hour" };
const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);
    apply();

    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", apply);
    else mediaQuery.addListener(apply);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", apply);
      else mediaQuery.removeListener(apply);
    };
  }, [breakpoint]);

  return isMobile;
}

const toDate = (v) => (v instanceof Date ? v : new Date(v));
const sameRange = (a, b) =>
  a && b && a.start?.toISOString?.() === b.start?.toISOString?.() && a.end?.toISOString?.() === b.end?.toISOString?.();

const hsl = (id) => `hsl(${Math.round(((Number(id) * 137.508) % 360))}deg 80% 45%)`;
const hexToRgba = (hex, alpha = 0.18) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return null;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16),
      g = parseInt(h.slice(2, 4), 16),
      b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (h.length === 8) {
    const r = parseInt(h.slice(0, 2), 16),
      g = parseInt(h.slice(2, 4), 16),
      b = parseInt(h.slice(4, 6), 16),
      a = parseInt(h.slice(6, 8), 16) / 255;
    return `rgba(${r}, ${g}, ${b}, ${Math.min(1, a * alpha)})`;
  }
  return null;
};
const colorToRgba = (color, alpha = 0.18) => {
  if (!color) return `rgba(0,0,0,${alpha})`;
  if (color.startsWith("#")) return hexToRgba(color, alpha) || `rgba(0,0,0,${alpha})`;
  if (color.startsWith("hsl")) return color.replace("hsl", "hsla").replace(")", ` / ${alpha})`);
  return `rgba(0,0,0,${alpha})`;
};

const hslToHex = (value) => {
  const match = String(value || "").match(
    /hsl\(\s*([\d.]+)(?:deg)?\s+([\d.]+)%\s+([\d.]+)%\s*\)/i
  );
  if (!match) return null;

  const h = ((Number(match[1]) % 360) + 360) % 360;
  const s = Math.max(0, Math.min(100, Number(match[2]))) / 100;
  const l = Math.max(0, Math.min(100, Number(match[3]))) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;

  if (hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];

  const m = l - c / 2;
  const toHex = (n) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
};

const colorInputHex = (value) => {
  if (/^#([0-9a-f]{6})$/i.test(String(value || ""))) return value;
  if (/^#([0-9a-f]{3})$/i.test(String(value || ""))) {
    const h = String(value).slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  }
  return hslToHex(value) || "#1a73e8";
};
const arrayMove = (arr, from, to) => {
  const c = arr.slice();
  const [it] = c.splice(from, 1);
  c.splice(to, 0, it);
  return c;
};
const isNil = (v) => v === null || v === undefined;
const isActiveResource = (r) => r?.active !== false;

const startOfDayLocal = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const fmtHM = (d) => format(new Date(d), "HH:mm");

const buildMonthGrid = (date) => {
  const first = startOfMonth(date);
  const last = endOfMonth(date);

  const monthDays = eachDayOfInterval({ start: first, end: last });

  // Monday-first index 0..6
  const jsDay = first.getDay(); // Sun 0 .. Sat 6
  const leading = (jsDay + 6) % 7;

  const cells = [
    ...Array.from({ length: leading }, () => null),
    ...monthDays,
  ];

  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

/* ------------------------------ Toasts ------------------------------ */

function useToast() {
  const [msg, setMsg] = useState(null);
  const tRef = useRef(null);
  const show = useCallback((text, type = "info", timeout = 2400) => {
    if (tRef.current) clearTimeout(tRef.current);
    setMsg({ text, type });
    tRef.current = setTimeout(() => setMsg(null), timeout);
  }, []);
  const Toast = () =>
    msg ? (
      <div
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 5000,
          background: msg.type === "error" ? "#fee2e2" : msg.type === "success" ? "#dcfce7" : "#e0f2fe",
          color: msg.type === "error" ? "#991b1b" : msg.type === "success" ? "#14532d" : "#075985",
          border: "1px solid rgba(0,0,0,0.1)",
          padding: "10px 14px",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,.15)",
          fontWeight: 700,
        }}
      >
        {msg.text}
      </div>
    ) : null;
  return { show, Toast };
}

/* ------------------------------ Context Menu ------------------------------ */

function useClickAway(handler) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) handler?.();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") handler?.();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [handler]);
  return ref;
}

function ContextMenu({
  open,
  x,
  y,
  eventObj,
  resources,
  onChoose,
  onClose,
}) {
  const { theme } = useTheme();
  const ref = useClickAway(onClose);
  if (!open) return null;

  const accent = theme?.accent_color || theme?.accent || "var(--accent-color)";
  const surface = "#fff";
  const border = "#e5e7eb";
  const hoverBg = "#f8fafc";

  const wrap = {
    position: "fixed",
    zIndex: 2000,
    top: y,
    left: x,
    background: surface,
    border: `1px solid ${border}`,
    borderRadius: 12,
    boxShadow: "0 16px 40px rgba(0,0,0,.18)",
    minWidth: 260,
    overflow: "hidden",
    fontFamily: theme.fontBody || "var(--font-body)",
  };

  const header = {
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: `1px solid ${border}`,
  };
  const btn = {
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    border: "none",
    background: "transparent",
    fontWeight: 700,
    cursor: "pointer",
    color: accent,
  };
  const item = (label, cb, key) => (
    <button
      key={key || label}
      style={btn}
      onClick={() => {
        cb();
        onClose?.();
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {label}
    </button>
  );

  const isCo = !!eventObj?.isCo;

  return createPortal(
    <div ref={ref} style={wrap}>
      <div style={header}>Actions</div>
      {item(isCo ? "Unassign Co-Resource" : "Unassign", () => onChoose({ action: "unassign", eventObj }))}

      <div style={{ ...header, borderTop: `1px solid ${border}` }}>Reassign Main to…</div>
      <div style={{ maxHeight: 240, overflow: "auto" }}>
        {resources.map((r) =>
          item(
            r.resourceTitle,
            () => onChoose({ action: "reassign-main", resourceId: r.resourceId, eventObj }),
            `main-${r.resourceId}`
          )
        )}
      </div>

      {!eventObj?.isTask && (
        <>
          <div style={{ ...header, borderTop: `1px solid ${border}` }}>Reassign Co to…</div>
          <div style={{ maxHeight: 240, overflow: "auto" }}>
            {resources.map((r) =>
              item(
                r.resourceTitle,
                () => onChoose({ action: "reassign-co", resourceId: r.resourceId, eventObj }),
                `co-${r.resourceId}`
              )
            )}
          </div>
        </>
      )}
    </div>,
    document.body
  );
}

/* ------------------------------ Toolbar ------------------------------ */

const BTN_GENERAL = "bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white";
const BTN_ACTION = "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white";
const BTN_BASE = "rounded-lg font-bold cursor-pointer inline-flex items-center justify-center select-none";

function ThemedToolbar({ label, onNavigate, onView, view, onOpenSettings }) {
  const { theme } = useTheme();

  const viewBtn = (name, v) => {
    const active = String(view) === String(v);
    return (
      <button
        type="button"
        onClick={() => onView(v)}
        className={`${BTN_BASE} ${BTN_GENERAL}`}
        style={{
          padding: "6px 12px",
          marginRight: 8,
          ...(active ? { boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.35)" } : {}),
        }}
      >
        {name}
      </button>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: 8,
        borderBottom: "1px solid #e5e7eb",
        background: "#fff",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <button
          type="button"
          onClick={() => onNavigate("TODAY")}
          className={`${BTN_BASE} ${BTN_GENERAL}`}
          style={{ padding: "6px 12px", marginRight: 8 }}
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => onNavigate("PREV")}
          className={`${BTN_BASE} ${BTN_GENERAL}`}
          style={{ padding: "6px 10px", marginRight: 8 }}
          title="Previous"
        >
          <FaChevronLeft />
        </button>
        <button
          type="button"
          onClick={() => onNavigate("NEXT")}
          className={`${BTN_BASE} ${BTN_GENERAL}`}
          style={{ padding: "6px 10px", marginRight: 8 }}
          title="Next"
        >
          <FaChevronRight />
        </button>
      </div>

      <div style={{ fontWeight: 700, color: theme?.accent || "var(--accent-color)" }}>{label}</div>

      <div style={{ display: "flex", alignItems: "center" }}>
        {viewBtn("Day", Views.DAY)}
        {viewBtn("Week", Views.WEEK)}
        {viewBtn("Month", Views.MONTH)}
        {viewBtn("Agenda", Views.AGENDA)}
        <button
          type="button"
          onClick={onOpenSettings}
          className={`${BTN_BASE} ${BTN_ACTION}`}
          style={{ padding: "8px 14px", marginLeft: 4 }}
          title="Settings"
        >
          <FaCog style={{ marginRight: 6 }} /> Settings
        </button>
      </div>
    </div>
  );
}

/* ------------------------------ Settings Modal ------------------------------ */

function generalButtonProps(onClick, extra = {}) {
  return {
    onClick,
    type: "button",
    className: `${BTN_BASE} ${BTN_GENERAL}`,
    style: { padding: "8px 10px", ...extra?.style },
  };
}
function actionButtonProps(onClick, extra = {}) {
  return {
    onClick,
    type: "button",
    className: `${BTN_BASE} ${BTN_ACTION}`,
    style: { padding: "10px 18px", ...extra?.style },
  };
}

const accentColor = (theme) => theme?.accent_color || theme?.accent || "#ff7a00";
const outlineChipClass =
  "border rounded-lg font-semibold inline-flex items-center justify-center select-none bg-transparent";
const outlineChipSmallClass = `${outlineChipClass}`;

function SettingsModal({ open, onClose, resources, order, setOrder, colours, setColours, visibility, setVisibility, settings, setSettings, isMobile = false }) {
  const { theme } = useTheme();
  const [localOrder, setLocalOrder] = useState(order);
  const [localColours, setLocalCols] = useState(colours);
  const [localVis, setLocalVis] = useState(visibility || {});
  const [startHour, setStartHour] = useState(settings.startHour);
  const [endHour, setEndHour] = useState(settings.endHour);
  const [slotMode, setSlotMode] = useState(settings.slotMode);

  const allIds = useMemo(
    () => Array.from(new Set([...(localOrder || []), ...resources.map((r) => r.resourceId)])),
    [localOrder, resources]
  );

  useEffect(() => {
    setLocalOrder(() => {
      const base = Array.isArray(order) ? order.slice() : [];
      const miss = resources.map((r) => r.resourceId).filter((id) => !base.includes(id));
      return [...base, ...miss];
    });
    setLocalCols(colours);
    setLocalVis(() => {
      const next = { ...(visibility || {}) };
      resources.forEach((r) => {
        if (typeof next[r.resourceId] === "undefined") next[r.resourceId] = true;
      });
      return next;
    });
    setStartHour(settings.startHour);
    setEndHour(settings.endHour);
    setSlotMode(settings.slotMode);
  }, [open, order, colours, visibility, settings, resources]);

  if (!open) return null;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const saveAll = () => {
    const mergedOrder = (() => {
      const base = Array.from(new Set(localOrder));
      const miss = resources.map((r) => r.resourceId).filter((id) => !base.includes(id));
      return [...base, ...miss];
    })();

    const mergedVis = { ...localVis };
    mergedOrder.forEach((id) => {
      if (typeof mergedVis[id] === "undefined") mergedVis[id] = true;
    });

    const s = clamp(Number(startHour), 0, 23);
    const eRaw = clamp(Number(endHour), 1, 24);
    const e = eRaw <= s ? s + 1 : eRaw;

    setOrder(mergedOrder);
    setColours(localColours);
    setVisibility(mergedVis);
    setSettings({ startHour: s, endHour: e, slotMode });

    localStorage.setItem(LS_RESOURCE_ORDER_KEY, JSON.stringify(mergedOrder));
    localStorage.setItem(LS_RESOURCE_COLOURS_KEY, JSON.stringify(localColours));
    localStorage.setItem(LS_RESOURCE_VISIBILITY_KEY, JSON.stringify(mergedVis));
    localStorage.setItem(LS_CAL_SETTINGS_KEY, JSON.stringify({ startHour: s, endHour: e, slotMode }));

    onClose();
  };

  const toggleAll = (visible) => {
    const next = { ...localVis };
    resources.forEach((r) => {
      next[r.resourceId] = !!visible;
    });
    setLocalVis(next);
    setVisibility(next);
    localStorage.setItem(LS_RESOURCE_VISIBILITY_KEY, JSON.stringify(next));
  };

  const applyColourImmediate = (id, hex) => {
    const nextLocal = { ...localColours, [id]: hex };
    const nextParent = { ...colours, [id]: hex };
    setLocalCols(nextLocal);
    setColours(nextParent);
    localStorage.setItem(LS_RESOURCE_COLOURS_KEY, JSON.stringify(nextParent));
  };

  const applyVisibilityImmediate = (id, checked) => {
    const nextLocal = { ...localVis, [id]: checked };
    const nextParent = { ...visibility, [id]: checked };
    setLocalVis(nextLocal);
    setVisibility(nextParent);
    localStorage.setItem(LS_RESOURCE_VISIBILITY_KEY, JSON.stringify(nextParent));
  };

  const moveResourceImmediate = (from, to) => {
    if (from < 0 || to < 0 || from >= allIds.length || to >= allIds.length || from === to) return;

    const moved = arrayMove(allIds, from, to);

    // Mobile ordering is explicit rather than drag/drop. Apply it immediately,
    // matching the already-immediate colour and visibility controls.
    setLocalOrder(moved);
    setOrder(moved);
    localStorage.setItem(LS_RESOURCE_ORDER_KEY, JSON.stringify(moved));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: isMobile ? "flex-end" : "center",
        justifyContent: "center",
        padding: isMobile ? 0 : 12,
        fontFamily: theme.fontBody || "var(--font-body)",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? "100%" : 920,
          maxWidth: isMobile ? "100vw" : "96vw",
          maxHeight: isMobile ? "88vh" : "90vh",
          background: "#fff",
          borderRadius: isMobile ? "16px 16px 0 0" : 12,
          boxShadow: "0 12px 36px rgba(0,0,0,0.25)",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr",
          gap: isMobile ? 20 : 16,
          padding: isMobile ? 14 : 16,
          overflow: "auto",
        }}
      >
        <div>
          <h3 style={{ margin: 0, marginBottom: 8, color: theme.accent || "var(--accent-color)" }}>General</h3>
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "flex", gap: 12, flexDirection: isMobile ? "column" : "row" }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Working day start</label>
                <select
                  value={startHour}
                  onChange={(e) => setStartHour(Number(e.target.value))}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb" }}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Working day end</label>
                <select
                  value={endHour}
                  onChange={(e) => setEndHour(Number(e.target.value))}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb" }}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Slot mode</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["hour", "half", "ampm", "both"].map((m) => (
                  <label key={m} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="radio"
                      name="slotMode"
                      value={m}
                      checked={slotMode === m}
                      onChange={(e) => setSlotMode(e.target.value)}
                    />
                    <span style={{ textTransform: "uppercase" }}>{m}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                className={outlineChipClass}
                onClick={onClose}
                style={{
                  padding: "8px 10px",
                  color: accentColor(theme),
                  borderColor: accentColor(theme),
                }}
              >
                Cancel
              </button>
              <button type="button" {...actionButtonProps(saveAll)}>
                Save
              </button>
            </div>
          </div>
        </div>

        <div>
          <h3 style={{ margin: 0, marginBottom: 8, color: theme.accent || "var(--accent-color)" }}>
            Resources (order, colours & visibility)
          </h3>

          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <button type="button" {...generalButtonProps(() => toggleAll(true))}>
              Show All
            </button>
            <button type="button" {...generalButtonProps(() => toggleAll(false))}>
              Hide All
            </button>
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            {allIds.map((id, idx) => {
              const r = resources.find((x) => x.resourceId === id);
              if (!r) return null;
              const rawColor = localColours[id] || colours[id] || hsl(id);
              const colorHex = colorInputHex(rawColor);
              const visible = typeof localVis[id] === "boolean" ? localVis[id] : true;

              return (
                <div
                  key={id}
                  draggable={!isMobile}
                  onDragStart={
                    isMobile
                      ? undefined
                      : (e) => {
                          window.__modal_drag_idx = idx;
                          try {
                            e.dataTransfer.setData("text/plain", String(id));
                          } catch {}
                        }
                  }
                  onDragOver={isMobile ? undefined : (e) => e.preventDefault()}
                  onDrop={
                    isMobile
                      ? undefined
                      : () => {
                          const from = window.__modal_drag_idx;
                          if (typeof from !== "number" || from === idx) return;
                          const moved = arrayMove(allIds, from, idx);
                          setLocalOrder(moved);
                          delete window.__modal_drag_idx;
                        }
                  }
                  title={isMobile ? "Use the arrow buttons to change resource order" : "Drag to reorder (persists on Save)"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? 8 : 10,
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: "1px dashed #e5e7eb",
                    background: "#fafafa",
                    cursor: isMobile ? "default" : "grab",
                    userSelect: "none",
                  }}
                >
                  {isMobile ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        flex: "0 0 auto",
                      }}
                    >
                      <button
                        type="button"
                        aria-label={`Move ${r.resourceTitle} up`}
                        title="Move up"
                        disabled={idx === 0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          moveResourceImmediate(idx, idx - 1);
                        }}
                        style={{
                          width: 34,
                          height: 30,
                          borderRadius: 8,
                          border: "1px solid #d1d5db",
                          background: idx === 0 ? "#f3f4f6" : "#fff",
                          color: idx === 0 ? "#9ca3af" : accentColor(theme),
                          fontWeight: 900,
                          cursor: idx === 0 ? "default" : "pointer",
                          lineHeight: 1,
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${r.resourceTitle} down`}
                        title="Move down"
                        disabled={idx === allIds.length - 1}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          moveResourceImmediate(idx, idx + 1);
                        }}
                        style={{
                          width: 34,
                          height: 30,
                          borderRadius: 8,
                          border: "1px solid #d1d5db",
                          background: idx === allIds.length - 1 ? "#f3f4f6" : "#fff",
                          color: idx === allIds.length - 1 ? "#9ca3af" : accentColor(theme),
                          fontWeight: 900,
                          cursor: idx === allIds.length - 1 ? "default" : "pointer",
                          lineHeight: 1,
                        }}
                      >
                        ↓
                      </button>
                    </div>
                  ) : (
                    <span style={{ opacity: 0.6 }}>≡</span>
                  )}

                  <span style={{ width: 12, height: 12, borderRadius: 999, background: rawColor, flex: "0 0 auto" }} />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: isMobile ? 700 : undefined,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {r.resourceTitle}
                  </span>

                  <label
                    title="Visible in planner view"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginRight: isMobile ? 2 : 10,
                      flex: "0 0 auto",
                    }}
                  >
                    <input type="checkbox" checked={visible} onChange={(e) => applyVisibilityImmediate(id, e.target.checked)} />
                    <span style={{ fontSize: 12 }}>Show</span>
                  </label>

                  <input
                    type="color"
                    value={colorHex}
                    onChange={(e) => applyColourImmediate(id, e.target.value)}
                    title="Set colour"
                    style={{ cursor: "pointer", background: "transparent", border: "none", flex: "0 0 auto" }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Resource Day Matrix ------------------------------ */

function ResourceDayMatrix({
  titleMode,
  days,
  currentDate,
  visibleOrderedResources,
  colours,
  events,
  settings,
  onCreateAssignment,
  onMoveAssignment,
  onOpenFlyout,
  onOpenContextMenu,
  onReorderResources,
  buildTitle,
}) {
  const { theme } = useTheme();

  const startHour = settings.startHour ?? 6;
  const endHour = settings.endHour ?? 18;

  const headerCell = {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    color: "#6b7280",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    padding: "8px 10px",
  };

  const cellStyle = (rid) => ({
    border: "1px solid #e5e7eb",
    padding: 8,
    minHeight: 84,
    verticalAlign: "top",
    background: "#fff",
    position: "relative",
    boxShadow: `inset 0 0 0 2px ${colorToRgba(colours[rid] || "#cbd5e1", 0.06)}`,
  });

  const pillStyle = (rid, isCo) => ({
    border: `2px solid ${colours[rid] || "#94a3b8"}`,
    background: colorToRgba(colours[rid] || "#94a3b8", isCo ? 0.12 : 0.18),
    borderRadius: 8,
    padding: "6px 8px",
    fontSize: 12,
    fontWeight: 700,
    color: theme.accent || "var(--accent-color)",
    marginBottom: 6,
    cursor: "grab",
    userSelect: "none",
  });

  const eventsByCell = useMemo(() => {
    const m = new Map();
    for (const ev of events) {
      const rid = Number(ev.resourceId);
      const dayKey = startOfDayLocal(ev.start).toISOString().slice(0, 10);
      const key = `${dayKey}::${rid}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(ev);
    }
    for (const arr of m.values()) arr.sort((a, b) => new Date(a.start) - new Date(b.start));
    return m;
  }, [events]);

  const getCellEvents = (day, rid) => {
    const key = `${startOfDayLocal(day).toISOString().slice(0, 10)}::${Number(rid)}`;
    return eventsByCell.get(key) || [];
  };

  const getNextAvailableWindow = useCallback(
    (day, rid, durationMinutes = 60, excludeAssignmentKey = null) => {
      const dayStart = setHours(setMinutes(new Date(day), 0), startHour);
      const dayEnd = setHours(setMinutes(new Date(day), 0), endHour);
      const durationMs = Math.max(15, Number(durationMinutes || 60)) * 60 * 1000;

      const relevant = getCellEvents(day, rid)
        .filter((ev) => !excludeAssignmentKey || String(ev.id) !== String(excludeAssignmentKey))
        .slice()
        .sort((a, b) => new Date(a.start) - new Date(b.start));

      let cursor = new Date(dayStart);

      for (const ev of relevant) {
        const evStart = new Date(ev.start);
        const evEnd = new Date(ev.end);
        if (cursor.getTime() + durationMs <= evStart.getTime()) {
          return { start: cursor, end: new Date(cursor.getTime() + durationMs) };
        }
        if (evEnd > cursor) cursor = new Date(evEnd);
      }

      if (cursor.getTime() + durationMs <= dayEnd.getTime()) {
        return { start: cursor, end: new Date(cursor.getTime() + durationMs) };
      }

      const fallbackStart = new Date(dayStart);
      return { start: fallbackStart, end: new Date(fallbackStart.getTime() + durationMs) };
    },
    [startHour, endHour, eventsByCell]
  );

  const handleCellClick = (day, rid) => {
    const { start, end } = getNextAvailableWindow(day, rid, 60);
    onOpenFlyout(start, end, rid);
  };

  const allowDrop = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onDropToCell = async (e, day, rid) => {
    e.preventDefault();
    const draggedTask = window.__CAL_DRAG_TASK__;
    const draggedJob = window.__CAL_DRAG_JOB__;
    const draggedAssign = window.__MATRIX_DRAG_ASSIGNMENT__;

    if (draggedAssign?.id) {
      await onMoveAssignment(draggedAssign.id, rid, day, null);
      return;
    }
    const externalItem = draggedTask || draggedJob;
    if (externalItem?.id) {
      const duration = Number(externalItem.duration_minutes || externalItem.planned_duration_minutes || 60);
      const { start, end } = getNextAvailableWindow(day, rid, duration);
      await onCreateAssignment(externalItem, rid, start, end);
    }
  };

  const onPillDragStart = (evObj) => (e) => {
    window.__MATRIX_DRAG_ASSIGNMENT__ = {
      id: evObj.id,
      durationMinutes: Math.max(15, Math.round((new Date(evObj.end) - new Date(evObj.start)) / 60000) || 60),
    };
    try {
      e.dataTransfer.setData("text/plain", String(evObj.id));
      e.dataTransfer.setDragImage?.(e.currentTarget, 10, 10);
    } catch {}
    e.dataTransfer.effectAllowed = "move";
  };

  const onPillDragEnd = () => {
    setTimeout(() => {
      delete window.__MATRIX_DRAG_ASSIGNMENT__;
    }, 0);
  };

  const onPillContext = (ev, evObj) => {
    ev.preventDefault();
    onOpenContextMenu?.({ x: ev.clientX, y: ev.clientY, eventObj: evObj });
  };

  const headerDragStart = (visibleIdx) => (e) => {
    window.__header_drag_visible_idx = visibleIdx;
    try {
      e.dataTransfer.setData("text/plain", String(visibleIdx));
    } catch {}
  };
  const headerDragOver = (e) => e.preventDefault();
  const headerDrop = (visibleIdx) => (e) => {
    e.preventDefault();
    const fromVisible = window.__header_drag_visible_idx;
    delete window.__header_drag_visible_idx;
    if (typeof fromVisible !== "number" || fromVisible === visibleIdx) return;

    const visibleIds = visibleOrderedResources.map((r) => r.resourceId);
    const movedVisible = arrayMove(visibleIds, fromVisible, visibleIdx);
    onReorderResources?.(movedVisible);
  };

  return (
    <div style={{ overflow: "auto", height: "calc(100vh - 64px)" }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th style={{ ...headerCell, position: "sticky", top: 0, left: 0, zIndex: 3, width: 170 }}>
              {titleMode === "month" ? "Date" : "Day"}
            </th>
            {visibleOrderedResources.map((r, vIdx) => (
              <th
                key={r.resourceId}
                draggable
                onDragStart={headerDragStart(vIdx)}
                onDragOver={headerDragOver}
                onDrop={headerDrop(vIdx)}
                title="Drag to reorder columns"
                style={{ ...headerCell, position: "sticky", top: 0, zIndex: 2, textAlign: "center", cursor: "grab" }}
              >
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 999, background: colours[r.resourceId] || hsl(r.resourceId) }} />
                  <span style={{ fontWeight: 700, color: theme.accent || "var(--accent-color)" }}>{r.resourceTitle}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((day) => {
            const active = isSameDay(day, currentDate);
            return (
              <tr key={startOfDayLocal(day).toISOString()}>
                <td
                  style={{
                    ...cellStyle(null),
                    position: "sticky",
                    left: 0,
                    background: active ? "#eef6ff" : "#f9fafb",
                    zIndex: 1,
                    width: 170,
                  }}
                >
                  <div style={{ fontWeight: 700, color: theme.accent || "var(--accent-color)" }}>
                    {format(day, "EEE d MMM")}
                  </div>
                </td>

                {visibleOrderedResources.map((r) => {
                  const list = getCellEvents(day, r.resourceId);

                  return (
                    <td
                      key={`${startOfDayLocal(day).toISOString()}-${r.resourceId}`}
                      style={cellStyle(r.resourceId)}
                      onClick={() => handleCellClick(day, r.resourceId)}
                      onDragOver={allowDrop}
                      onDrop={(e) => onDropToCell(e, day, r.resourceId)}
                      title="Click to create, or drop here"
                    >
                      {list.map((ev) => (
                        <div
                          key={ev.id}
                          style={pillStyle(r.resourceId, ev.isCo)}
                          draggable
                          onDragStart={onPillDragStart(ev)}
                          onDragEnd={onPillDragEnd}
                          onContextMenu={(e) => onPillContext(e, ev)}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenFlyout(ev.start, ev.end, r.resourceId);
                          }}
                          data-matrix-pill
                        >
                          <div>{buildTitle(ev)}</div>
                          <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
                            {fmtHM(ev.start)} → {fmtHM(ev.end)}
                          </div>
                        </div>
                      ))}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


/* ------------------------------ Mobile Planner ------------------------------ */

function MobilePlannerToolbar({
  currentView,
  currentDate,
  unassignedCount,
  onView,
  onPrevious,
  onToday,
  onNext,
  onOpenUnassigned,
  onOpenSettings,
}) {
  const { theme } = useTheme();
  const accent = theme?.accent_color || theme?.accent || "#ff7a00";
  const views = [
    ["Day", Views.DAY],
    ["Week", Views.WEEK],
    ["Month", Views.MONTH],
    ["Agenda", Views.AGENDA],
  ];

  return (
    <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      <div style={{ padding: "10px 12px 8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: accent }}>
          {currentView === Views.MONTH || currentView === "month"
            ? format(currentDate, "MMMM yyyy")
            : currentView === Views.WEEK || currentView === "week"
              ? `${format(startOfWeek(currentDate, { weekStartsOn: 1 }), "d MMM")} – ${format(addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6), "d MMM yyyy")}`
              : format(currentDate, "EEEE d MMMM yyyy")}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 44px 44px 1fr", gap: 6, padding: 8 }}>
        <button type="button" className={`${BTN_BASE} ${BTN_GENERAL}`} style={{ minHeight: 42 }} onClick={onToday}>
          Today
        </button>
        <button type="button" className={`${BTN_BASE} ${BTN_GENERAL}`} style={{ minHeight: 42 }} onClick={onPrevious} title="Previous">
          <FaChevronLeft />
        </button>
        <button type="button" className={`${BTN_BASE} ${BTN_GENERAL}`} style={{ minHeight: 42 }} onClick={onNext} title="Next">
          <FaChevronRight />
        </button>
        <button type="button" className={`${BTN_BASE} ${BTN_ACTION}`} style={{ minHeight: 42 }} onClick={onOpenSettings}>
          <FaCog style={{ marginRight: 6 }} /> Settings
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, padding: "0 8px 8px" }}>
        {views.map(([label, value]) => {
          const active = String(currentView) === String(value);
          return (
            <button
              key={label}
              type="button"
              className={`${BTN_BASE} ${BTN_GENERAL}`}
              onClick={() => onView(value)}
              style={{
                minHeight: 40,
                padding: "7px 4px",
                opacity: active ? 1 : 0.72,
                boxShadow: active ? "inset 0 0 0 2px rgba(255,255,255,0.38)" : "none",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div style={{ padding: "0 8px 8px" }}>
        <button
          type="button"
          className={`${BTN_BASE} ${BTN_ACTION}`}
          onClick={onOpenUnassigned}
          style={{ width: "100%", minHeight: 42, justifyContent: "center" }}
        >
          Unassigned Jobs ({unassignedCount})
        </button>
      </div>
    </div>
  );
}

function MobileWeekStrip({ currentDate, events, onSelectDay }) {
  const { theme } = useTheme();
  const accent = theme?.accent_color || theme?.accent || "#ff7a00";
  const start = startOfWeek(currentDate, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end: addDays(start, 6) });

  const eventCountFor = (day) =>
    events.filter((ev) => !ev.isCo && isSameDay(new Date(ev.start), day)).length;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
        gap: 4,
        background: "#fff",
        borderRadius: 12,
        padding: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      {days.map((day) => {
        const active = isSameDay(day, currentDate);
        const count = eventCountFor(day);
        return (
          <button
            key={day.toISOString()}
            type="button"
            onClick={() => onSelectDay(day)}
            style={{
              border: active ? "none" : `1px solid ${accent}`,
              background: active ? accent : "#fff",
              color: active ? "#fff" : accent,
              borderRadius: 10,
              padding: "7px 2px 6px",
              minHeight: 62,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.9 }}>{format(day, "EEE")}</div>
            <div style={{ fontSize: 16, lineHeight: 1.25 }}>{format(day, "d")}</div>
            <div style={{ fontSize: 10, opacity: 0.9 }}>{count ? `${count} job${count === 1 ? "" : "s"}` : "—"}</div>
          </button>
        );
      })}
    </div>
  );
}

function MobileMonthGrid({ currentDate, events, onSelectDay }) {
  const { theme } = useTheme();
  const accent = theme?.accent_color || theme?.accent || "#ff7a00";
  const cells = buildMonthGrid(currentDate);

  const eventCountFor = (day) =>
    events.filter((ev) => !ev.isCo && isSameDay(new Date(ev.start), day)).length;

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 5 }}>
        {weekdayNames.map((wd) => (
          <div key={wd} style={{ textAlign: "center", fontSize: 10, fontWeight: 800, color: "#64748b" }}>
            {wd.slice(0, 1)}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`blank-${idx}`} style={{ minHeight: 48 }} />;
          const active = isSameDay(day, currentDate);
          const count = eventCountFor(day);
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onSelectDay(day)}
              style={{
                minHeight: 48,
                borderRadius: 9,
                border: active ? "none" : "1px solid #e5e7eb",
                background: active ? accent : "#fff",
                color: active ? "#fff" : "#111827",
                padding: "4px 2px",
                cursor: "pointer",
                fontWeight: active ? 800 : 600,
              }}
            >
              <div>{format(day, "d")}</div>
              <div style={{ fontSize: 9, minHeight: 12, opacity: active ? 0.95 : 0.7 }}>
                {count ? `${count}` : ""}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MobileResourceStack({
  day,
  resources,
  colours,
  events,
  buildTitle,
  onOpenActions,
}) {
  const { theme } = useTheme();

  const dayEvents = useMemo(
    () =>
      events
        .filter((ev) => isSameDay(new Date(ev.start), day))
        .slice()
        .sort((a, b) => new Date(a.start) - new Date(b.start)),
    [events, day]
  );

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: theme?.accent || "var(--accent-color)", padding: "2px 2px 0" }}>
        {format(day, "EEEE d MMMM")}
      </div>

      {resources.map((resource) => {
        const resourceEvents = dayEvents.filter((ev) => Number(ev.resourceId) === Number(resource.resourceId));
        const colour = colours[resource.resourceId] || hsl(resource.resourceId);

        return (
          <section
            key={resource.resourceId}
            style={{
              background: "#fff",
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "10px 12px",
                borderBottom: resourceEvents.length ? "1px solid #e5e7eb" : "none",
                background: colorToRgba(colour, 0.08),
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ width: 10, height: 10, borderRadius: 999, background: colour, flex: "0 0 auto" }} />
                <span style={{ fontWeight: 800, color: theme?.accent || "var(--accent-color)", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {resource.resourceTitle}
                </span>
              </div>
              <span style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>
                {resourceEvents.length ? `${resourceEvents.length} assignment${resourceEvents.length === 1 ? "" : "s"}` : "Available"}
              </span>
            </div>

            {resourceEvents.length === 0 ? (
              <div style={{ padding: "11px 12px", fontSize: 12, color: "#64748b" }}>No assignments.</div>
            ) : (
              <div style={{ display: "grid", gap: 8, padding: 9 }}>
                {resourceEvents.map((ev) => (
                  <button
                    key={String(ev.id)}
                    type="button"
                    onClick={() => onOpenActions(ev)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      border: `2px solid ${colour}`,
                      background: colorToRgba(colour, ev.isCo ? 0.12 : 0.18),
                      borderRadius: 10,
                      padding: "9px 10px",
                      cursor: "pointer",
                      color: theme?.accent || "var(--accent-color)",
                    }}
                  >
                    <div style={{ fontWeight: 800, lineHeight: 1.25 }}>{buildTitle(ev)}</div>
                    <div style={{ fontSize: 11, opacity: 0.78, marginTop: 3 }}>
                      {fmtHM(ev.start)} → {fmtHM(ev.end)}
                      {ev.isCo ? " · Co-resource" : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function MobileAgenda({ events, resources, colours, buildTitle, onOpenActions }) {
  const resourceById = useMemo(() => new Map(resources.map((r) => [Number(r.resourceId), r])), [resources]);
  const sorted = useMemo(
    () => events.slice().sort((a, b) => new Date(a.start) - new Date(b.start)),
    [events]
  );

  if (!sorted.length) {
    return <div style={{ background: "#fff", borderRadius: 12, padding: 16, color: "#64748b" }}>No assignments in this range.</div>;
  }

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {sorted.map((ev) => {
        const resource = resourceById.get(Number(ev.resourceId));
        const colour = colours[ev.resourceId] || hsl(ev.resourceId);
        return (
          <button
            key={String(ev.id)}
            type="button"
            onClick={() => onOpenActions(ev)}
            style={{
              textAlign: "left",
              width: "100%",
              borderRadius: 12,
              border: `2px solid ${colour}`,
              background: "#fff",
              padding: 11,
              cursor: "pointer",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", marginBottom: 3 }}>
              {format(new Date(ev.start), "EEE d MMM · HH:mm")} – {fmtHM(ev.end)}
            </div>
            <div style={{ fontWeight: 800 }}>{buildTitle(ev)}</div>
            <div style={{ fontSize: 11, marginTop: 4, color: "#64748b" }}>
              {resource?.resourceTitle || `Resource ${ev.resourceId}`}{ev.isCo ? " · Co-resource" : ""}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function MobileUnassignedJobs({
  jobs,
  jobTypesById,
  filter,
  onFilter,
  onAssign,
  onClose,
}) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>Unassigned Work</div>
        <button type="button" className={`${BTN_BASE} ${BTN_GENERAL}`} style={{ minHeight: 40, padding: "8px 12px" }} onClick={onClose}>
          Back
        </button>
      </div>

      <input
        type="text"
        placeholder="Filter by job ref, location or type…"
        value={filter}
        onChange={(e) => onFilter(e.target.value)}
        style={{ width: "100%", minHeight: 44, padding: "9px 10px", borderRadius: 10, border: "1px solid #d1d5db" }}
      />

      {jobs.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, padding: 16, color: "#64748b" }}>No unassigned work.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {jobs.map((job) => {
            const typeLabel =
              jobTypesById[Number(job.job_type_id)]?.short_code ||
              jobTypesById[Number(job.job_type_id)]?.name ||
              "";
            return (
              <div
                key={job.id}
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 11,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 900, textTransform: "uppercase", color: job.isTask ? "#475569" : "#64748b" }}>
                  {job.isTaskList ? "Task List" : job.isTask ? `Workflow Task${job.execution_state === "awaiting_reassignment" ? " · Awaiting Reassignment" : ""}` : "Job"}
                </div>
                <div style={{ fontWeight: 800 }}>
                  {job.isTaskList ? `${job.job_ref || `Job #${job.job_id}`} (#${job.job_id}) / ${job.work_package_label || `WP-#${job.work_package_id}`} — ${job.title} (${job.task_count || 0} tasks)` : job.isTask ? [job.job_ref || job.parent_job_ref, job.title || job.task_ref || `Task #${job.id}`].filter(Boolean).join(" - ") : (job.job_ref || `Job #${job.id}`)}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                  {job.isTask ? (job.status || "Ready for assignment") : ([job.location_name || "", typeLabel].filter(Boolean).join(" · ") || "No additional details")}
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
                  {job.isTaskList ? `${job.task_count || 0} tasks` : `${Number(job.planned_duration_minutes || job.duration_minutes || 60)} minutes`}
                </div>
                <button
                  type="button"
                  className={`${BTN_BASE} ${BTN_ACTION}`}
                  onClick={() => onAssign(job)}
                  style={{ width: "100%", minHeight: 42, marginTop: 10 }}
                >
                  Assign
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MobileAssignSheet({
  job,
  resources,
  currentDate,
  settings,
  onClose,
  onConfirm,
}) {
  const [resourceId, setResourceId] = useState(() => Number(resources?.[0]?.resourceId || 0));
  const [dateValue, setDateValue] = useState(() => format(currentDate || new Date(), "yyyy-MM-dd"));
  const [timeValue, setTimeValue] = useState(() => `${String(settings?.startHour ?? 8).padStart(2, "0")}:00`);
  const [duration, setDuration] = useState(() => Number(job?.planned_duration_minutes || job?.duration_minutes || 60));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setResourceId(Number(resources?.[0]?.resourceId || 0));
    setDateValue(format(currentDate || new Date(), "yyyy-MM-dd"));
    setTimeValue(`${String(settings?.startHour ?? 8).padStart(2, "0")}:00`);
    setDuration(Number(job?.planned_duration_minutes || job?.duration_minutes || 60));
  }, [job, resources, currentDate, settings?.startHour]);

  if (!job) return null;

  const submit = async () => {
    if (!resourceId || !dateValue || !timeValue) return;
    const start = new Date(`${dateValue}T${timeValue}:00`);
    const end = new Date(start.getTime() + Math.max(15, Number(duration || 60)) * 60000);
    setSaving(true);
    try {
      await onConfirm(job, Number(resourceId), start, end);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3200,
        background: "rgba(0,0,0,0.38)",
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxHeight: "88vh",
          overflow: "auto",
          background: "#fff",
          borderRadius: "18px 18px 0 0",
          padding: 14,
          boxShadow: "0 -10px 30px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ width: 44, height: 4, background: "#d1d5db", borderRadius: 999, margin: "0 auto 12px" }} />
        <div style={{ fontSize: 18, fontWeight: 800 }}>
          Assign {job.isTask ? (job.title || job.task_ref || `Task #${job.id}`) : (job.job_ref || `Job #${job.id}`)}
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 3, marginBottom: 14 }}>
          Choose the resource and planned time. Mobile assignment does not require drag and drop.
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <label style={{ display: "grid", gap: 5, fontWeight: 700 }}>
            Resource
            <select
              value={resourceId}
              onChange={(e) => setResourceId(Number(e.target.value))}
              style={{ minHeight: 44, borderRadius: 10, border: "1px solid #d1d5db", padding: "8px 10px" }}
            >
              {resources.map((r) => (
                <option key={r.resourceId} value={r.resourceId}>{r.resourceTitle}</option>
              ))}
            </select>
          </label>

          <label style={{ display: "grid", gap: 5, fontWeight: 700 }}>
            Date
            <input
              type="date"
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
              style={{ minHeight: 44, borderRadius: 10, border: "1px solid #d1d5db", padding: "8px 10px" }}
            />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label style={{ display: "grid", gap: 5, fontWeight: 700 }}>
              Start
              <input
                type="time"
                value={timeValue}
                onChange={(e) => setTimeValue(e.target.value)}
                style={{ minHeight: 44, borderRadius: 10, border: "1px solid #d1d5db", padding: "8px 10px" }}
              />
            </label>

            <label style={{ display: "grid", gap: 5, fontWeight: 700 }}>
              Duration
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                style={{ minHeight: 44, borderRadius: 10, border: "1px solid #d1d5db", padding: "8px 10px" }}
              >
                {[30, 45, 60, 90, 120, 180, 240, 480].map((m) => (
                  <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} hr${m === 60 ? "" : "s"}`}</option>
                ))}
              </select>
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 4 }}>
            <button type="button" className={outlineChipClass} onClick={onClose} style={{ minHeight: 44, borderColor: "#64748b", color: "#475569" }}>
              Cancel
            </button>
            <button type="button" className={`${BTN_BASE} ${BTN_ACTION}`} onClick={submit} disabled={saving || !resourceId} style={{ minHeight: 44, opacity: saving ? 0.65 : 1 }}>
              {saving ? "Assigning…" : job.isTask ? "Assign Task" : "Assign Job"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function MobileAssignmentActions({
  eventObj,
  resources,
  onClose,
  onUnassign,
  onReassign,
}) {
  const [resourceId, setResourceId] = useState(() => Number(eventObj?.resourceId || resources?.[0]?.resourceId || 0));
  if (!eventObj) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3150,
        background: "rgba(0,0,0,0.38)",
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          background: "#fff",
          borderRadius: "18px 18px 0 0",
          padding: 14,
          boxShadow: "0 -10px 30px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ width: 44, height: 4, background: "#d1d5db", borderRadius: 999, margin: "0 auto 12px" }} />
        <div style={{ fontSize: 17, fontWeight: 800 }}>Assignment actions</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, marginBottom: 12 }}>
          {fmtHM(eventObj.start)} → {fmtHM(eventObj.end)}{eventObj.isCo ? " · Co-resource" : ""}
        </div>

        <label style={{ display: "grid", gap: 5, fontWeight: 700 }}>
          Reassign to
          <select
            value={resourceId}
            onChange={(e) => setResourceId(Number(e.target.value))}
            style={{ minHeight: 44, borderRadius: 10, border: "1px solid #d1d5db", padding: "8px 10px" }}
          >
            {resources.map((r) => (
              <option key={r.resourceId} value={r.resourceId}>{r.resourceTitle}</option>
            ))}
          </select>
        </label>

        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <button
            type="button"
            className={`${BTN_BASE} ${BTN_GENERAL}`}
            style={{ minHeight: 44 }}
            onClick={() => onReassign(eventObj, resourceId)}
          >
            Reassign
          </button>
          <button
            type="button"
            style={{
              minHeight: 44,
              borderRadius: 10,
              border: "1px solid #ef4444",
              background: "#fff",
              color: "#b91c1c",
              fontWeight: 800,
              cursor: "pointer",
            }}
            onClick={() => onUnassign(eventObj)}
          >
            {eventObj.isCo ? "Unassign Co-Resource" : "Unassign Job"}
          </button>
          <button type="button" className={outlineChipClass} style={{ minHeight: 44, borderColor: "#64748b", color: "#475569" }} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}


/* ------------------------------ Page Component ------------------------------ */

export default function OrganisationalCalendar() {
  const { theme } = useTheme();
  const { show: toast, Toast } = useToast();
  const isMobile = useIsMobile();
  const [mobileSurface, setMobileSurface] = useState("schedule");
  const [mobileAssignJob, setMobileAssignJob] = useState(null);
  const [mobileActionEvent, setMobileActionEvent] = useState(null);

  const [resources, setResources] = useState([]);
  const [order, setOrder] = useState([]);
  const [colours, setColours] = useState({});
  const [visibility, setVisibility] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LS_RESOURCE_VISIBILITY_KEY) || "{}");
    } catch {
      return {};
    }
  });
  const [settings, setSettings] = useState(() => {
    try {
      return { ...DEFAULT_SETTINGS, ...(JSON.parse(localStorage.getItem(LS_CAL_SETTINGS_KEY) || "{}")) };
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const [events, setEvents] = useState([]);
  const [taskEvents, setTaskEvents] = useState([]);
  const [unassignedTasks, setUnassignedTasks] = useState([]);
  const [daysWithEvents, setDaysWithEvents] = useState(new Set());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState("week");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [monthCursor, setMonthCursor] = useState(startOfMonth(new Date()));
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [unassigned, setUnassigned] = useState([]);
  const [filterUnassigned, setFilterUnassigned] = useState("");
  const [overlapsAllowed, setOverlapsAllowed] = useState(null);

  const [menuState, setMenuState] = useState({ open: false, x: 0, y: 0, targetEvent: null });

  const lastRangeRef = useRef(null);
  const creatingAssignmentRef = useRef(new Set());

  const [customersById, setCustomersById] = useState({});
  const [jobTypesById, setJobTypesById] = useState({});
  const [jobsById, setJobsById] = useState({});

  const [showWeek, setShowWeek] = useState(() => localStorage.getItem(LS_COLLAPSE_WEEK_KEY) === "1");
  const [showMonths, setShowMonths] = useState(() => localStorage.getItem(LS_COLLAPSE_MONTHS_KEY) === "1");

  useEffect(() => {
    localStorage.setItem(LS_COLLAPSE_WEEK_KEY, showWeek ? "1" : "0");
  }, [showWeek]);

  useEffect(() => {
    localStorage.setItem(LS_COLLAPSE_MONTHS_KEY, showMonths ? "1" : "0");
  }, [showMonths]);

  useEffect(() => {
    setMonthCursor(startOfMonth(currentDate));
  }, [currentDate]);

  const mergeVisibleReorder = useCallback((visibleIdsNew) => {
    setOrder((prevFull) => {
      const visibleSet = new Set(prevFull.filter((id) => visibleIdsNew.includes(id)));
      const hidden = prevFull.filter((id) => !visibleSet.has(id));
      const visIter = visibleIdsNew[Symbol.iterator]();
      const next = prevFull.map((id) => (hidden.includes(id) ? id : visIter.next().value));
      localStorage.setItem(LS_RESOURCE_ORDER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setCalendarView = useCallback((v) => {
    const s = String(v).toLowerCase();
    const norm = s === "day" || s === "week" || s === "month" || s === "agenda" ? s : "week";
    setCurrentView(norm);
    setTimeout(() => window.dispatchEvent(new Event("resize")), 0);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let res;
        try {
          res = await axiosInstance.get("/api/resources", { params: { include_inactive: false } });
        } catch {
          res = await axiosInstance.get("/api/resources");
        }

        let list = Array.isArray(res.data) ? res.data : res.data.items || [];
        list = list.filter(isActiveResource);

        const map = list.map((r) => ({
          resourceId: Number(r.id),
          resourceTitle:
            (r.name && String(r.name)) ||
            [r.first_name, r.last_name].filter(Boolean).join(" ") ||
            `Resource ${r.id}`,
        }));

        if (!mounted) return;

        setResources(map);

        const savedOrder = JSON.parse(localStorage.getItem(LS_RESOURCE_ORDER_KEY) || "[]");
        const baseOrder = Array.isArray(savedOrder) && savedOrder.length ? savedOrder : map.map((r) => r.resourceId);
        const merged = Array.from(new Set([...baseOrder, ...map.map((r) => r.resourceId)]));
        setOrder(merged);
        localStorage.setItem(LS_RESOURCE_ORDER_KEY, JSON.stringify(merged));

        const savedColours = JSON.parse(localStorage.getItem(LS_RESOURCE_COLOURS_KEY) || "{}");
        const seeded = { ...savedColours };
        map.forEach((r) => {
          if (!seeded[r.resourceId]) seeded[r.resourceId] = hsl(r.resourceId);
        });
        setColours(seeded);
        localStorage.setItem(LS_RESOURCE_COLOURS_KEY, JSON.stringify(seeded));

        const visRaw = (() => {
          try {
            return JSON.parse(localStorage.getItem(LS_RESOURCE_VISIBILITY_KEY) || "{}");
          } catch {
            return {};
          }
        })();
        const nextVis = { ...visRaw };
        map.forEach((r) => {
          if (typeof nextVis[r.resourceId] === "undefined") nextVis[r.resourceId] = true;
        });
        setVisibility(nextVis);
        localStorage.setItem(LS_RESOURCE_VISIBILITY_KEY, JSON.stringify(nextVis));
      } catch (e) {
        console.error("Resource load failed:", e);
        if (mounted) {
          setError("Failed to load resources");
          toast("Failed to load resources", "error");
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [toast]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await axiosInstance.get("/api/tenant-settings");
        const allow = !!(resp?.data?.allow_overlaps ?? resp?.data?.calendar_allow_overlaps);
        if (active) setOverlapsAllowed(allow);
      } catch {
        if (active) setOverlapsAllowed(null);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [custRes, jtRes] = await Promise.all([
          axiosInstance.get("/customers"),
          axiosInstance.get("/job-types?sort=name:asc"),
        ]);
        if (!alive) return;

        const cMap = {};
        (Array.isArray(custRes.data) ? custRes.data : []).forEach((c) => {
          cMap[Number(c.id)] = c;
        });
        const jtMap = {};
        (Array.isArray(jtRes.data) ? jtRes.data : []).forEach((t) => {
          jtMap[Number(t.id)] = t;
        });

        setCustomersById(cMap);
        setJobTypesById(jtMap);
      } catch (e) {
        console.warn("Lookup fetch failed:", e?.response?.data || e?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const orderedResources = useMemo(() => {
    const mp = new Map(resources.map((r) => [r.resourceId, r]));
    const base = order.map((id) => mp.get(id)).filter(Boolean);
    const missing = resources.filter((r) => !order.includes(r.resourceId));
    return [...base, ...missing];
  }, [resources, order]);

  const visibleOrderedResources = useMemo(
    () => orderedResources.filter((r) => visibility[r.resourceId] !== false),
    [orderedResources, visibility]
  );

  const buildTitle = (a) => {
    const job = jobsById[Number(a.job_id)] || a.job || {};
    const jobRef = a.job_ref || job.job_ref || `Job #${a.job_id}`;
    const locName =
      job?.location?.name ||
      job?.location_name ||
      (job?.location_id ? `Location #${job.location_id}` : "");
    const jt = jobTypesById[Number(job.job_type_id)];
    const jtName = jt?.short_code || jt?.name || "";
    return [jobRef, locName, jtName, a.isCo ? "(Co)" : null].filter(Boolean).join(" - ");
  };

  const taskAssignmentUrl = (task, assignmentId = null) => {
    const base = `/api/jobs/${Number(task.job_id)}/work-packages/${Number(task.work_package_id)}/tasks/${Number(task.task_id || task.id)}/assignments`;
    return assignmentId ? `${base}/${Number(assignmentId)}` : base;
  };

  const taskListContextLabel = (item) => `${item.job_ref || `Job #${item.job_id}`} (#${item.job_id}) / ${item.work_package_label || `WP-#${item.work_package_id}`}`;
  const taskListLabel = (item) => `${taskListContextLabel(item)} — ${item.title} (${item.task_count || 0} tasks)`;

  const taskProjectionToEvent = (item) => {
    if (!item?.starts_at || !item?.ends_at || !item?.resource_id) return null;
    return {
      id: item.item_type === "task_list" ? `tasklist::${Number(item.id)}` : `task::${Number(item.task_id || item.id)}`,
      title: item.item_type === "task_list" ? taskListLabel(item) : (item.title || item.task_ref || `Task #${item.task_id || item.id}`),
      start: toDate(item.starts_at),
      end: toDate(item.ends_at),
      resourceId: Number(item.resource_id),
      job_id: Number(item.job_id),
      job_ref: item.job_ref || item.parent_job_ref,
      isCo: false,
      isTask: true,
      isTaskList: item.item_type === "task_list",
      task_list_id: item.item_type === "task_list" ? Number(item.id) : null,
      task_list_assignment_id: item.task_list_assignment_id || null,
      task_count: item.task_count || null,
      task_id: item.item_type === "task_list" ? null : Number(item.task_id || item.id),
      work_package_id: Number(item.work_package_id),
      workflow_id: item.workflow_id ? Number(item.workflow_id) : null,
      task_assignment_id: item.task_assignment_id ? Number(item.task_assignment_id) : null,
      assignment_source: item.assignment_source,
      execution_state: item.execution_state || "available",
      status: item.status,
      task_ref: item.task_ref,
      task_key: item.task_key,
    };
  };

  const mapAssignments = (items) => {
    const out = [];
    for (const a of items) {
      const base = {
        id: Number(a.id),
        title: "",
        start: toDate(a.starts_at),
        end: toDate(a.ends_at),
        resourceId: Number(a.resource_id),
        job_id: Number(a.job_id),
        role: a.role,
        notes: a.notes,
        isCo: false,
        job_ref: a.job_ref,
        job: a.job,
      };
      out.push(base);

      const coId = Number(a.co_resource_id || a.coResourceId || a.co_resource);
      if (coId) {
        out.push({
          ...base,
          id: `${a.id}::co`,
          resourceId: coId,
          isCo: true,
        });
      }
    }
    return out;
  };

  useEffect(() => {
    const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const set = new Set([...events, ...taskEvents].map((e) => key(e.start)));
    setDaysWithEvents(set);
  }, [events, taskEvents]);

  const computeRange = (view, date) => {
    if (view === Views.DAY || view === "day") {
      const s = new Date(date);
      s.setHours(0, 0, 0, 0);
      const e = new Date(s);
      e.setDate(e.getDate() + 1);
      return { start: s, end: e };
    }
    if (view === Views.WEEK || view === "week") {
      const s = startOfWeek(date, { weekStartsOn: 1 });
      const e = addDays(s, 7);
      return { start: s, end: e };
    }
    if (view === Views.MONTH || view === "month") {
      const s = startOfMonth(date);
      const e = addMonths(s, 1);
      return { start: s, end: e };
    }
    const s = startOfWeek(date, { weekStartsOn: 1 });
    const e = addDays(s, 28);
    return { start: s, end: e };
  };

  const prefetchJobs = async (items) => {
    const ids = [...new Set(items.map((a) => Number(a.job_id)).filter(Boolean))];
    const missing = ids.filter((id) => !jobsById[id]);
    if (!missing.length) return;

    const fetched = {};
    try {
      const batch = await axiosInstance.get("/api/jobs", { params: { ids: missing.join(",") } });
      const arr = Array.isArray(batch.data) ? batch.data : batch.data?.items || [];
      arr.forEach((j) => {
        fetched[Number(j.id)] = j;
      });
    } catch {}

    const still = missing.filter((id) => !fetched[id]);
    if (still.length) {
      const results = await Promise.allSettled(still.map((id) => axiosInstance.get(`/api/jobs/${id}`)));
      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value?.data) {
          const j = r.value.data;
          fetched[Number(j.id)] = j;
        }
      });
    }

    if (Object.keys(fetched).length) setJobsById((prev) => ({ ...prev, ...fetched }));
  };

  const fetchAssignments = async (range) => {
    if (!range) return;
    const norm = Array.isArray(range)
      ? { start: new Date(Math.min(...range.map((d) => +d))), end: new Date(Math.max(...range.map((d) => +d))) }
      : range;
    if (sameRange(norm, lastRangeRef.current)) return;
    lastRangeRef.current = norm;

    try {
      const resp = await axiosInstance.get("/api/job-assignments", {
        params: { start: norm.start.toISOString(), end: norm.end.toISOString() },
      });
      const items = (resp.data && resp.data.items) || [];

      await prefetchJobs(items);

      const mapped = mapAssignments(items);
      setEvents(mapped);

      if (mapped.length > 0) {
        const earliest = mapped.reduce((min, e) => (e.start < min ? e.start : min), mapped[0].start);
        const anyInView = mapped.some((e) => e.start < norm.end && e.end > norm.start);
        if (!anyInView) {
          setCurrentDate(earliest);
          setMonthCursor(startOfMonth(earliest));
          setInfo(`No assignments in current view; centered on ${earliest.toLocaleString()}.`);
        } else {
          setInfo(null);
        }
      } else {
        setInfo("No assignments in this range.");
      }
    } catch (e) {
      console.error("Assignments fetch failed:", e);
      setError("Failed to load assignments");
      toast("Failed to load assignments", "error");
    }
  };

  const fetchTaskProjection = useCallback(async () => {
    try {
      const resp = await axiosInstance.get("/api/planner/work-items", {
        params: { include_jobs: false, include_tasks: true },
      });
      const items = Array.isArray(resp.data) ? resp.data : resp.data?.items || [];
      const tasks = items.filter((item) => item?.item_type === "task" || item?.item_type === "task_list");

      const assigned = tasks.map(taskProjectionToEvent).filter(Boolean);
      setTaskEvents(assigned);

      const unassignedItems = tasks
        .filter((item) => !item?.resource_id || item?.execution_state === "awaiting_reassignment")
        .map((item) => ({
          ...item,
          id: Number(item.task_id || item.id),
          task_id: item.item_type === "task_list" ? null : Number(item.task_id || item.id),
          task_list_id: item.item_type === "task_list" ? Number(item.id) : null,
          task_list_assignment_id: item.task_list_assignment_id || null,
          isTaskList: item.item_type === "task_list",
          task_count: item.task_count || 0,
          job_id: Number(item.job_id),
          work_package_id: Number(item.work_package_id),
          isTask: true,
          duration_minutes:
            item.item_type === "task_list" ? 60 : item.starts_at && item.ends_at
              ? Math.max(15, Math.round((toDate(item.ends_at) - toDate(item.starts_at)) / 60000) || 60)
              : 60,
        }));
      setUnassignedTasks(unassignedItems);
    } catch (e) {
      console.error("Workflow task planner projection fetch failed:", e);
      toast("Failed to load workflow tasks", "error");
    }
  }, [toast]);

  const fetchUnassigned = useCallback(
    async (range) => {
      const paramsBase = {
        limit: 500,
        start: range?.start?.toISOString?.(),
        end: range?.end?.toISOString?.(),
      };

      const strictFilter = (jobs, byJobId) =>
        jobs.filter((j) => {
          const jid = Number(j.id);
          const arr = byJobId.get(jid) || [];
          if (!arr.length) return true;
          return arr.every((a) => isNil(a?.resource_id));
        });

      let candidate = [];
      try {
        const [r1, r2] = await Promise.allSettled([
          axiosInstance.get("/api/jobs", { params: { ...paramsBase, unassigned: 1 } }),
          axiosInstance.get("/api/jobs", { params: { ...paramsBase, assigned_resource_is_null: 1 } }),
        ]);
        if (r1.status === "fulfilled") candidate = Array.isArray(r1.value.data) ? r1.value.data : r1.value.data?.items || [];
        else if (r2.status === "fulfilled") candidate = Array.isArray(r2.value.data) ? r2.value.data : r2.value.data?.items || [];
      } catch {}

      if (!candidate.length) {
        try {
          const r3 = await axiosInstance.get("/api/jobs", { params: paramsBase });
          candidate = Array.isArray(r3.data) ? r3.data : r3.data?.items || [];
        } catch (e) {
          console.warn("Jobs fetch failed:", e?.response?.data || e?.message);
          setUnassigned([]);
          return;
        }
      }

      const ids = [...new Set(candidate.map((j) => Number(j.id)).filter(Boolean))];
      const byJobId = new Map();

      let checked = false;
      if (ids.length) {
        try {
          const bat = await axiosInstance.get("/api/job-assignments", { params: { job_ids: ids.join(",") } });
          const arr = Array.isArray(bat.data) ? bat.data : bat.data?.items || [];
          arr.forEach((a) => {
            const jid = Number(a.job_id);
            if (!byJobId.has(jid)) byJobId.set(jid, []);
            byJobId.get(jid).push(a);
          });
          checked = true;
        } catch {}
      }

      if (!checked && ids.length) {
        const results = await Promise.allSettled(ids.map((jid) => axiosInstance.get(`/api/jobs/${jid}/assignments`)));
        results.forEach((r, i) => {
          const jid = ids[i];
          if (r.status === "fulfilled") {
            const arr = Array.isArray(r.value.data) ? r.value.data : r.value.data?.items || [];
            byJobId.set(jid, arr);
          } else {
            byJobId.set(jid, []);
          }
        });
        checked = true;
      }

      let filtered = candidate;
      if (checked) filtered = strictFilter(candidate, byJobId);

      const assignedInView = new Set(events.map((e) => Number(e.job_id)));
      filtered = filtered.filter((j) => !assignedInView.has(Number(j.id)));

      const mapped = filtered.map((j) => ({
        id: Number(j.id),
        job_ref: j.job_ref || `Job #${j.id}`,
        customer_id: j.customer_id,
        job_type_id: j.job_type_id,
        location_id: j.location_id,
        location_name: j.location?.name || j.location_name || "",
        duration_minutes: Number(j.planned_duration_minutes || j.duration_minutes || 60),
        week_commencing: j.week_commencing || null,
      }));

      setUnassigned(mapped);
    },
    [events]
  );

  useEffect(() => {
    const r = computeRange(currentView, currentDate);
    (async () => {
      await fetchAssignments(r);
      await Promise.all([fetchUnassigned(r), fetchTaskProjection()]);
    })();
  }, [currentView, currentDate, customersById, jobTypesById]); // eslint-disable-line

  useEffect(() => {
    const onRefresh = async () => {
      const r = computeRange(currentView, currentDate);
      await fetchAssignments(r);
      await Promise.all([fetchUnassigned(r), fetchTaskProjection()]);
    };
    window.addEventListener("calendar:refresh", onRefresh);
    return () => window.removeEventListener("calendar:refresh", onRefresh);
  }, [currentView, currentDate, fetchUnassigned, fetchTaskProjection]); // eslint-disable-line

  const parseKey = (keyLike) => {
    if (typeof keyLike === "number") return { id: keyLike, isCo: false };
    const s = String(keyLike || "");
    if (s.startsWith("tasklist::")) return { id: Number(s.split("::")[1]), isCo: false, isTask: true, isTaskList: true };
    if (s.startsWith("task::")) return { id: Number(s.split("::")[1]), isCo: false, isTask: true };
    if (s.includes("::co")) return { id: Number(s.split("::")[0]), isCo: true };
    if (s.endsWith("-co")) return { id: Number(s.replace("-co", "")), isCo: true };
    return { id: Number(s), isCo: false, isTask: false };
  };

  const refreshPlannerData = async () => {
    const r = computeRange(currentView, currentDate);
    await Promise.all([fetchAssignments(r), fetchUnassigned(r), fetchTaskProjection()]);
  };

  const taskConflictDetail = (e) => {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (detail?.message) return detail.message;
    return "Resource is unavailable for this task window.";
  };

  const withTaskConflictOverride = async (operation, e) => {
    if (e?.response?.status !== 409) throw e;
    const detail = taskConflictDetail(e);
    const proceed = window.confirm(`${detail}\n\nOverride this availability conflict?`);
    if (!proceed) return false;
    const reason = window.prompt("Reason for overriding the availability conflict:", "");
    if (!reason?.trim()) {
      toast("Conflict override cancelled: a reason is required.", "error");
      return false;
    }
    await operation({ allow_conflict_override: true, override_reason: reason.trim() });
    return true;
  };

  const taskListAssignmentUrl = (task, assignmentId = null) => {
    const base = `/api/jobs/${Number(task.job_id)}/work-packages/${Number(task.work_package_id)}/task-lists/${Number(task.task_list_id || task.id)}/assignments`;
    return assignmentId ? `${base}/${Number(assignmentId)}` : base;
  };

  const createTaskAssignment = async (task, resourceId, start, end, override = {}) => {
    const payload = {
      resource_id: Number(resourceId),
      assignment_role: "primary",
      starts_at: toDate(start).toISOString(),
      ends_at: toDate(end).toISOString(),
      ...override,
    };
    const op = (extra = {}) => axiosInstance.post(task.isTaskList ? taskListAssignmentUrl(task) : taskAssignmentUrl(task), { ...payload, ...extra });
    try {
      await op();
    } catch (e) {
      const overridden = await withTaskConflictOverride(op, e);
      if (!overridden) throw e;
    }
    await refreshPlannerData();
  };

  const updateTaskAssignment = async (task, resourceId, start, end, override = {}) => {
    if (!(task.isTaskList ? task.task_list_assignment_id : task.task_assignment_id)) {
      await createTaskAssignment(task, resourceId, start, end, override);
      return;
    }
    const payload = {
      resource_id: Number(resourceId),
      starts_at: toDate(start).toISOString(),
      ends_at: toDate(end).toISOString(),
      ...override,
    };
    const op = (extra = {}) => axiosInstance.patch(task.isTaskList ? taskListAssignmentUrl(task, task.task_list_assignment_id) : taskAssignmentUrl(task, task.task_assignment_id), { ...payload, ...extra });
    try {
      await op();
    } catch (e) {
      const overridden = await withTaskConflictOverride(op, e);
      if (!overridden) throw e;
    }
    await refreshPlannerData();
  };

  const unassignTask = async (task) => {
    if (!(task?.isTaskList ? task.task_list_assignment_id : task?.task_assignment_id)) {
      toast("Task has no explicit assignment to remove.", "error");
      return;
    }
    await axiosInstance.post(`${task.isTaskList ? taskListAssignmentUrl(task, task.task_list_assignment_id) : taskAssignmentUrl(task, task.task_assignment_id)}/deactivate`, {
      reason: "Unassigned from organisational planner",
    });
    await refreshPlannerData();
    toast("Task moved to Unassigned", "success");
  };

  const unassignAssignment = async (assignmentKey, jobId) => {
    const parsed = parseKey(assignmentKey);
    const { id, isCo, isTask, isTaskList } = parsed;
    if (isTask) {
      const task = taskEvents.find((e) => isTaskList ? Number(e.task_list_id) === Number(id) : Number(e.task_id) === Number(id));
      if (!task) return;
      try {
        await unassignTask(task);
      } catch (e) {
        toast(e?.response?.data?.detail || "Failed to unassign task", "error");
      }
      return;
    }

    if (isCo) {
      try {
        await axiosInstance.patch(`/api/job-assignments/${id}`, { co_resource_id: null });
        setEvents((list) => list.filter((e) => !(e.isCo && parseKey(e.id).id === id)));
        toast("Co-Resource unassigned", "success");
      } catch {
        toast("Failed to unassign co-resource", "error");
      }
      const r = computeRange(currentView, currentDate);
      await Promise.all([fetchAssignments(r), fetchUnassigned(r)]);
      return;
    }

    let hardError = false;
    try {
      const resp = await axiosInstance.delete(`/api/jobs/${jobId}/resources/${id}`);
      if (resp && resp.status >= 400) hardError = true;
      setEvents((list) => list.filter((e) => parseKey(e.id).id !== id));
    } catch {
      hardError = true;
    }

    const r = computeRange(currentView, currentDate);
    await Promise.all([fetchAssignments(r), fetchUnassigned(r)]);

    if (!hardError) toast("Job unassigned", "success");
    else toast("Failed to unassign job", "error");
  };

  const reassignAssignment = async (assignmentKey, resourceId, kind) => {
    const parsed = parseKey(assignmentKey);
    const { id, isTask, isTaskList } = parsed;
    if (isTask) {
      const task = taskEvents.find((e) => isTaskList ? Number(e.task_list_id) === Number(id) : Number(e.task_id) === Number(id));
      if (!task) return;
      if (kind === "co") {
        toast("Workflow task co-resource assignment is not enabled in this planner pass.", "error");
        return;
      }
      try {
        await updateTaskAssignment(task, resourceId, task.start, task.end);
        toast("Task reassigned", "success");
      } catch (e) {
        if (e?.response?.status !== 409) toast(e?.response?.data?.detail || "Failed to reassign task", "error");
      }
      return;
    }
    try {
      const body = kind === "co" ? { co_resource_id: Number(resourceId) } : { resource_id: Number(resourceId) };
      await axiosInstance.patch(`/api/job-assignments/${id}`, body);
      setEvents((list) =>
        list.map((e) => {
          const k = parseKey(e.id);
          if (k.id !== id) return e;
          if (kind === "co" && e.isCo) return { ...e, resourceId: Number(resourceId) };
          if (kind === "main" && !e.isCo) return { ...e, resourceId: Number(resourceId) };
          return e;
        })
      );
      toast("Job reassigned", "success");
    } catch {
      toast("Failed to reassign", "error");
    }
  };

  const onEventDrop = async ({ event, start, end, resourceId }) => {
    if (event?.isTask) {
      try {
        await updateTaskAssignment(event, Number(resourceId || event.resourceId), start, end);
        toast("Task moved", "success");
      } catch (e) {
        if (e?.response?.status !== 409) toast(e?.response?.data?.detail || "Failed to move task", "error");
      }
      return;
    }
    const prev = events;
    const { id, isCo } = parseKey(event.id);

    try {
      const body = {
        starts_at: toDate(start).toISOString(),
        ends_at: toDate(end).toISOString(),
      };
      if (resourceId) {
        if (isCo) body.co_resource_id = Number(resourceId);
        else body.resource_id = Number(resourceId);
      }
      await axiosInstance.patch(`/api/job-assignments/${id}`, body);

      setEvents((list) =>
        list.map((e) => {
          const k = parseKey(e.id);
          if (k.id !== id) return e;
          const next = { ...e, start: toDate(start), end: toDate(end) };
          if (resourceId && k.isCo === isCo) next.resourceId = Number(resourceId);
          return next;
        })
      );
    } catch (e) {
      if (e?.response?.status === 409) {
        const msg = overlapsAllowed ? "Unavailability conflict." : "Resource already booked in this period (overlap).";
        toast(`❌ ${msg}`, "error");
      } else {
        toast("❌ Failed to update assignment.", "error");
      }
      setEvents(prev);
    }
  };

  const onEventResize = async ({ event, start, end }) => {
    if (event?.isTask) {
      try {
        await updateTaskAssignment(event, event.resourceId, start, end);
        toast("Task timing updated", "success");
      } catch (e) {
        if (e?.response?.status !== 409) toast(e?.response?.data?.detail || "Failed to resize task", "error");
      }
      return;
    }
    const prev = events;
    const { id } = parseKey(event.id);
    try {
      await axiosInstance.patch(`/api/job-assignments/${id}`, {
        starts_at: toDate(start).toISOString(),
        ends_at: toDate(end).toISOString(),
      });
      setEvents((list) => list.map((e) => (parseKey(e.id).id === id ? { ...e, start: toDate(start), end: toDate(end) } : e)));
    } catch (e) {
      if (e?.response?.status === 409) {
        const msg = overlapsAllowed ? "Unavailability conflict." : "Resource already booked in this period (overlap).";
        toast(`❌ ${msg}`, "error");
      } else {
        toast("❌ Failed to resize assignment.", "error");
      }
      setEvents(prev);
    }
  };

  const beginDragUnassigned = (job, e) => {
    if (job?.isTask) {
      window.__CAL_DRAG_TASK__ = job;
    } else {
      window.__CAL_DRAG_JOB__ = job;
    }
    window.__RBC_EXTERNAL_DRAG__ = true;
    try {
      e?.dataTransfer?.setData?.(job?.isTaskList ? "application/x-vision-task-list-id" : job?.isTask ? "application/x-vision-task-id" : "application/x-vision-job-id", String(job.id));
      e?.dataTransfer?.setData?.("text/plain", String(job.id));
      e.dataTransfer.effectAllowed = "copyMove";
    } catch {}
    document.body.classList.add("ext-drag");
  };

  const endDragUnassigned = () => {
    document.body.classList.remove("ext-drag");
    setTimeout(() => {
      delete window.__CAL_DRAG_JOB__;
      delete window.__CAL_DRAG_TASK__;
      delete window.__RBC_EXTERNAL_DRAG__;
    }, 80);
  };

  const createAssignmentOnce = async (jobId, payload) => {
    const inFlight = creatingAssignmentRef.current;
    if (inFlight.has(jobId)) return;
    inFlight.add(jobId);
    try {
      await axiosInstance.post(`/api/jobs/${jobId}/resources`, payload);
      setUnassigned((list) => list.filter((j) => Number(j.id) !== Number(jobId)));
    } finally {
      inFlight.delete(jobId);
    }
  };

  const onDropFromOutsideRBC = async ({ start, end, resource }) => {
    const draggedTask = window.__CAL_DRAG_TASK__;
    const dragged = window.__CAL_DRAG_JOB__;
    if (!dragged && !draggedTask) return;

    const resourceId = Number(resource ?? orderedResources?.[0]?.resourceId ?? null);
    if (!resourceId) {
      toast("Please drop onto a specific resource column.", "error");
      return;
    }

    const starts_at = toDate(start).toISOString();
    const ends_at = toDate(end).toISOString();

    try {
      if (draggedTask) {
        await createTaskAssignment(draggedTask, resourceId, start, end);
        toast("Task assigned", "success");
        return;
      }
      await createAssignmentOnce(dragged.id, { resource_id: resourceId, starts_at, ends_at, status: "planned" });
      const r = computeRange(currentView, currentDate);
      await Promise.all([fetchAssignments(r), fetchUnassigned(r)]);
      toast("Job assigned", "success");
    } catch (e) {
      if (e?.response?.status === 409) {
        const detail = e?.response?.data?.detail || "Conflict.";
        toast(detail, "error");
      } else {
        toast("Failed to create assignment.", "error");
      }
    } finally {
      endDragUnassigned();
    }
  };

  const getNextAvailableWindowForResourceDay = useCallback(
    (resourceId, day, durationMinutes = 60, excludeAssignmentKey = null) => {
      const startHour = settings.startHour ?? 6;
      const endHour = settings.endHour ?? 18;
      const dayStart = setHours(setMinutes(new Date(day), 0), startHour);
      const dayEnd = setHours(setMinutes(new Date(day), 0), endHour);
      const durationMs = Math.max(15, Number(durationMinutes || 60)) * 60 * 1000;

      const sameCell = [...events, ...taskEvents]
        .filter((ev) => {
          if (Number(ev.resourceId) !== Number(resourceId)) return false;
          if (!isSameDay(new Date(ev.start), new Date(day))) return false;
          if (excludeAssignmentKey && String(ev.id) === String(excludeAssignmentKey)) return false;
          return true;
        })
        .slice()
        .sort((a, b) => new Date(a.start) - new Date(b.start));

      let cursor = new Date(dayStart);

      for (const ev of sameCell) {
        const evStart = new Date(ev.start);
        const evEnd = new Date(ev.end);

        if (cursor.getTime() + durationMs <= evStart.getTime()) {
          return { start: cursor, end: new Date(cursor.getTime() + durationMs) };
        }
        if (evEnd > cursor) cursor = new Date(evEnd);
      }

      if (cursor.getTime() + durationMs <= dayEnd.getTime()) {
        return { start: cursor, end: new Date(cursor.getTime() + durationMs) };
      }

      const fallbackStart = new Date(dayStart);
      return { start: fallbackStart, end: new Date(fallbackStart.getTime() + durationMs) };
    },
    [events, taskEvents, settings]
  );

  const moveAssignmentToResourceDay = async (assignmentKey, resourceId, day) => {
    const parsed = parseKey(assignmentKey);
    const { id, isCo, isTask, isTaskList } = parsed;
    const allPlannerEvents = [...events, ...taskEvents];
    const currentEv = allPlannerEvents.find((ev) => String(ev.id) === String(assignmentKey));
    const prev = isTask ? taskEvents : events;
    const durationMinutes = currentEv ? Math.max(15, Math.round((new Date(currentEv.end) - new Date(currentEv.start)) / 60000) || 60) : 60;
    const { start, end } = getNextAvailableWindowForResourceDay(resourceId, day, durationMinutes, assignmentKey);

    try {
      if (isTask) {
        if (!currentEv) return;
        await updateTaskAssignment(currentEv, resourceId, start, end);
        toast("Task moved", "success");
        return;
      }
      const body = {
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        ...(isCo ? { co_resource_id: Number(resourceId) } : { resource_id: Number(resourceId) }),
      };
      await axiosInstance.patch(`/api/job-assignments/${id}`, body);
      setEvents((list) =>
        list.map((ev) => {
          const k = parseKey(ev.id);
          if (k.id !== id) return ev;
          const next = { ...ev, start, end };
          if (k.isCo === isCo) next.resourceId = Number(resourceId);
          return next;
        })
      );
      const r = computeRange(currentView, currentDate);
      await fetchUnassigned(r);
      toast("Job moved", "success");
    } catch (e) {
      if (e?.response?.status === 409) {
        const detail =
          e?.response?.data?.detail || (overlapsAllowed ? "Unavailability conflict." : "Overlap not allowed.");
        toast(detail, "error");
      } else {
        toast("Failed to move assignment.", "error");
      }
      if (isTask) setTaskEvents(prev);
      else setEvents(prev);
    } finally {
      setTimeout(() => {
        delete window.__MATRIX_DRAG_ASSIGNMENT__;
      }, 0);
    }
  };

  const createAssignmentForResourceDay = async (jobId, resourceId, start, end) => {
    try {
      await createAssignmentOnce(jobId, {
        resource_id: Number(resourceId),
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        status: "planned",
      });
      const r = computeRange(currentView, currentDate);
      await Promise.all([fetchAssignments(r), fetchUnassigned(r)]);
      toast("Job assigned", "success");
    } catch (e) {
      if (e?.response?.status === 409) {
        const detail = e?.response?.data?.detail || "Conflict.";
        toast(detail, "error");
      } else {
        toast("Failed to create assignment.", "error");
      }
    }
  };

  const createPlannerItemForResourceDay = async (item, resourceId, start, end) => {
    if (item?.isTask) {
      try {
        if (item.task_assignment_id || item.task_list_assignment_id) await updateTaskAssignment(item, resourceId, start, end);
        else await createTaskAssignment(item, resourceId, start, end);
        toast("Task assigned", "success");
      } catch (e) {
        if (e?.response?.status !== 409) toast(e?.response?.data?.detail || "Failed to assign task", "error");
      }
      return;
    }
    await createAssignmentForResourceDay(item.id, resourceId, start, end);
  };

  const openFlyoutWithRange = (start, end, resourceId) => {
    const payload = {
      starts_at: toDate(start).toISOString(),
      ends_at: toDate(end).toISOString(),
      resource_id: Number(resourceId ?? orderedResources[0]?.resourceId ?? null),
    };
    if (typeof window.__GJCF_PREFILL === "function") {
      try {
        window.__GJCF_PREFILL(payload);
        return;
      } catch {}
    }
    try {
      window.dispatchEvent(new CustomEvent("calendar:prefill-job", { detail: payload }));
    } catch (err) {
      console.error("Failed to emit prefill event:", err);
    }
  };

  const onSelectSlot = (slotInfo) => openFlyoutWithRange(slotInfo.start, slotInfo.end, slotInfo.resourceId);
  const onSelectEvent = (event) => openFlyoutWithRange(event.start, event.end, event.resourceId);

  const eventPropGetter = (event) => {
    const resColor = colours[event.resourceId] || "var(--primary-color, #e63946)";
    const needsAttention = event?.isTask && event?.execution_state === "awaiting_reassignment";
    return {
      style: {
        border: needsAttention ? "2px solid #b91c1c" : `2px solid ${resColor}`,
        background: needsAttention ? "#fee2e2" : colorToRgba(resColor, event.isCo ? 0.12 : 0.18),
        borderRadius: 8,
        fontFamily: theme.fontBody || "var(--font-body)",
        color: theme.accent || "var(--accent-color)",
        fontWeight: 700,
      },
    };
  };

  const handleSelectDay = (d) => {
    setCurrentDate(d);
    setMonthCursor(startOfMonth(d));
    setCalendarView(Views.DAY);
  };

  const handleSelectMonth = (mIndex) => {
    const base = new Date(currentDate.getFullYear(), mIndex, 1);
    setMonthCursor(startOfMonth(base));
    setCurrentDate(base);
    setCalendarView(Views.MONTH);
  };

  const filteredUnassigned = useMemo(() => {
    if (!filterUnassigned) return unassigned;
    const q = filterUnassigned.toLowerCase();
    return unassigned.filter((j) => {
      const jobRef = (j.job_ref || "").toLowerCase();
      const loc = (j.location_name || "").toLowerCase();
      const typeName = (jobTypesById[Number(j.job_type_id)]?.short_code || jobTypesById[Number(j.job_type_id)]?.name || "").toLowerCase();
      return [jobRef, loc, typeName].some((s) => s.includes(q));
    });
  }, [unassigned, filterUnassigned, jobTypesById]);

  const filteredUnassignedTasks = useMemo(() => {
    if (!filterUnassigned) return unassignedTasks;
    const q = filterUnassigned.toLowerCase();
    return unassignedTasks.filter((t) =>
      [t.title, t.task_ref, t.job_ref, t.parent_job_ref, t.status, t.execution_state]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [unassignedTasks, filterUnassigned]);

  const visibleWeekCommencing = useMemo(() => {
    if (!(currentView === Views.WEEK || currentView === "week")) return null;
    return format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
  }, [currentView, currentDate]);

  const isInVisiblePlanningWeek = useCallback(
    (item) => {
      if (!visibleWeekCommencing || !item?.week_commencing) return false;
      return String(item.week_commencing).slice(0, 10) === visibleWeekCommencing;
    },
    [visibleWeekCommencing]
  );

  const weekRailUnassigned = useMemo(
    () => (visibleWeekCommencing ? unassigned.filter(isInVisiblePlanningWeek) : []),
    [unassigned, visibleWeekCommencing, isInVisiblePlanningWeek]
  );

  const weekRailUnassignedTasks = useMemo(
    () => (visibleWeekCommencing ? unassignedTasks.filter(isInVisiblePlanningWeek) : []),
    [unassignedTasks, visibleWeekCommencing, isInVisiblePlanningWeek]
  );

  const leftRailUnassigned = useMemo(
    () =>
      visibleWeekCommencing
        ? filteredUnassigned.filter((item) => !isInVisiblePlanningWeek(item))
        : filteredUnassigned,
    [filteredUnassigned, visibleWeekCommencing, isInVisiblePlanningWeek]
  );

  const leftRailUnassignedTasks = useMemo(
    () =>
      visibleWeekCommencing
        ? filteredUnassignedTasks.filter((item) => !isInVisiblePlanningWeek(item))
        : filteredUnassignedTasks,
    [filteredUnassignedTasks, visibleWeekCommencing, isInVisiblePlanningWeek]
  );

  const handleContextChoose = async ({ action, resourceId, eventObj }) => {
    if (!eventObj?.id) return;
    if (action === "unassign") {
      await unassignAssignment(eventObj.id, eventObj.job_id);
    } else if (action === "reassign-main" && resourceId) {
      await reassignAssignment(eventObj.id, resourceId, "main");
    } else if (action === "reassign-co" && resourceId) {
      await reassignAssignment(eventObj.id, resourceId, "co");
    }
  };

  const accent = theme?.accent_color || theme?.accent || "#ff7a00";

  const plannerDays = useMemo(() => {
    if (currentView === Views.WEEK || currentView === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end: addDays(start, 6) });
    }
    if (currentView === Views.MONTH || currentView === "month") {
      return eachDayOfInterval({
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate),
      });
    }
    return [];
  }, [currentView, currentDate]);

  const toolbarLabel = useMemo(() => {
    if (currentView === Views.MONTH || currentView === "month") return format(currentDate, "MMMM yyyy");
    return format(currentDate, "EEEE d MMM yyyy");
  }, [currentView, currentDate]);

  const navigateMobile = useCallback(
    (direction) => {
      if (direction === "today") {
        const now = new Date();
        setCurrentDate(now);
        setMonthCursor(startOfMonth(now));
        return;
      }

      const step = direction === "previous" ? -1 : 1;
      let next;
      if (currentView === Views.MONTH || currentView === "month") next = addMonths(currentDate, step);
      else if (currentView === Views.WEEK || currentView === "week") next = addDays(currentDate, step * 7);
      else if (currentView === Views.AGENDA || currentView === "agenda") next = addDays(currentDate, step * 7);
      else next = addDays(currentDate, step);

      setCurrentDate(next);
      setMonthCursor(startOfMonth(next));
    },
    [currentDate, currentView]
  );

  const handleMobileAssign = useCallback(
    async (job, resourceId, start, end) => {
      await createPlannerItemForResourceDay(job, resourceId, start, end);
      setCurrentDate(start);
      setMonthCursor(startOfMonth(start));
      setMobileAssignJob(null);
      setMobileSurface("schedule");
    },
    [createPlannerItemForResourceDay]
  );

  const handleMobileUnassign = useCallback(
    async (eventObj) => {
      await unassignAssignment(eventObj.id, eventObj.job_id);
      setMobileActionEvent(null);
    },
    [unassignAssignment]
  );

  const handleMobileReassign = useCallback(
    async (eventObj, resourceId) => {
      await reassignAssignment(eventObj.id, resourceId, eventObj.isCo ? "co" : "main");
      setMobileActionEvent(null);
    },
    [reassignAssignment]
  );

  if (isMobile) {
    const mobileShowDayStack =
      currentView === Views.DAY ||
      currentView === "day" ||
      currentView === Views.WEEK ||
      currentView === "week" ||
      currentView === Views.MONTH ||
      currentView === "month";

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f5f5f7",
          padding: "8px 12px 18px",
          fontFamily: theme.fontBody || "var(--font-body)",
          boxSizing: "border-box",
        }}
      >
        <style>{`
          body.ext-drag * { cursor: default !important; }
        `}</style>

        <MobilePlannerToolbar
          currentView={currentView}
          currentDate={currentDate}
          unassignedCount={filteredUnassigned.length + filteredUnassignedTasks.length}
          onView={(view) => {
            setCalendarView(view);
            setMobileSurface("schedule");
          }}
          onPrevious={() => navigateMobile("previous")}
          onToday={() => navigateMobile("today")}
          onNext={() => navigateMobile("next")}
          onOpenUnassigned={() => setMobileSurface("unassigned")}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        {info && (
          <div style={{ marginTop: 8, background: "#eef6ff", border: "1px solid #b3d4ff", borderRadius: 10, padding: 10, color: "#084298" }}>
            {info}
          </div>
        )}
        {error && (
          <div style={{ marginTop: 8, background: "#fff", border: "1px solid #ffcccc", borderRadius: 10, padding: 10, color: "#a10000" }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          {mobileSurface === "unassigned" ? (
            <MobileUnassignedJobs
              jobs={[...filteredUnassignedTasks, ...filteredUnassigned]}
              jobTypesById={jobTypesById}
              filter={filterUnassigned}
              onFilter={setFilterUnassigned}
              onAssign={(job) => setMobileAssignJob(job)}
              onClose={() => setMobileSurface("schedule")}
            />
          ) : (
            <>
              {(currentView === Views.WEEK || currentView === "week") && (
                <MobileWeekStrip
                  currentDate={currentDate}
                  events={[...events, ...taskEvents]}
                  onSelectDay={(day) => {
                    setCurrentDate(day);
                    setMonthCursor(startOfMonth(day));
                  }}
                />
              )}

              {(currentView === Views.MONTH || currentView === "month") && (
                <MobileMonthGrid
                  currentDate={currentDate}
                  events={[...events, ...taskEvents]}
                  onSelectDay={(day) => {
                    setCurrentDate(day);
                    setMonthCursor(startOfMonth(day));
                  }}
                />
              )}

              {mobileShowDayStack && (
                <div style={{ marginTop: currentView === Views.DAY || currentView === "day" ? 0 : 10 }}>
                  <MobileResourceStack
                    day={currentDate}
                    resources={visibleOrderedResources}
                    colours={colours}
                    events={[...events, ...taskEvents]}
                    buildTitle={(ev) => (ev.isTask ? ev.title : buildTitle(ev))}
                    onOpenActions={(eventObj) => setMobileActionEvent(eventObj)}
                  />
                </div>
              )}

              {(currentView === Views.AGENDA || currentView === "agenda") && (
                <MobileAgenda
                  events={[...events, ...taskEvents]}
                  resources={visibleOrderedResources}
                  colours={colours}
                  buildTitle={(ev) => (ev.isTask ? ev.title : buildTitle(ev))}
                  onOpenActions={(eventObj) => setMobileActionEvent(eventObj)}
                />
              )}
            </>
          )}
        </div>

        <MobileAssignSheet
          job={mobileAssignJob}
          resources={visibleOrderedResources.length ? visibleOrderedResources : orderedResources}
          currentDate={currentDate}
          settings={settings}
          onClose={() => setMobileAssignJob(null)}
          onConfirm={handleMobileAssign}
        />

        <MobileAssignmentActions
          eventObj={mobileActionEvent}
          resources={visibleOrderedResources.length ? visibleOrderedResources : orderedResources}
          onClose={() => setMobileActionEvent(null)}
          onUnassign={handleMobileUnassign}
          onReassign={handleMobileReassign}
        />

        <SettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          resources={resources}
          order={order}
          setOrder={setOrder}
          colours={colours}
          setColours={setColours}
          visibility={visibility}
          setVisibility={setVisibility}
          settings={settings}
          setSettings={setSettings}
          isMobile
        />

        <Toast />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        paddingLeft: 24,
        paddingRight: 24,
        minHeight: "100vh",
        background: "#f5f5f7",
        fontFamily: theme.fontBody || "var(--font-body)",
      }}
      onContextMenu={(e) => {
        if ((e.target.closest && e.target.closest(".rbc-event")) || e.target.getAttribute?.("data-matrix-pill")) return;
      }}
    >
      <style>{`
        .rbc-time-header .rbc-header { text-align: center; }
        .rbc-header { display: flex; justify-content: center; align-items: center; }
        .rbc-date-cell { text-align: center; }
        .ext-drag * { cursor: grabbing !important; }
      `}</style>

      <aside
        style={{
          width: 320,
          flex: "0 0 320px",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          padding: 16,
          height: "calc(100vh - 48px)",
          position: "sticky",
          top: 16,
          alignSelf: "flex-start",
          overflow: "auto",
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              marginBottom: 8,
              color: theme.accent || "var(--accent-color)",
              textAlign: "center",
            }}
          >
            {format(startOfMonth(monthCursor), "MMMM yyyy")}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 6,
              marginBottom: 6,
              fontSize: 12,
              color: theme.accent || "var(--accent-color)",
              opacity: 0.9,
            }}
          >
            {weekdayNames.map((wd) => (
              <div key={wd} style={{ textAlign: "center" }}>
                {wd}
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
            {buildMonthGrid(monthCursor).map((d, idx) => {
              if (!d) return <div key={`blank-${idx}`} />;

              const isActive = isSameDay(d, currentDate);
              const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              const hasEvents = daysWithEvents.has(key);

              return (
                <button
                  key={key}
                  type="button"
                  title={format(d, "EEE d MMM")}
                  onClick={() => handleSelectDay(d)}
                  className={isActive ? `${BTN_BASE} ${BTN_GENERAL}` : outlineChipSmallClass}
                  style={
                    isActive
                      ? { justifyContent: "center", padding: "6px 0" }
                      : { justifyContent: "center", padding: "6px 0", borderColor: accent, color: accent }
                  }
                >
                  <span style={{ position: "relative" }}>
                    {format(d, "d")}
                    {hasEvents ? (
                      <span
                        style={{
                          position: "absolute",
                          bottom: -6,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 5,
                          height: 5,
                          borderRadius: 999,
                          background: accent,
                        }}
                      />
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "center" }}>
            <button
              type="button"
              onClick={() => {
                const d = addMonths(monthCursor, -1);
                setMonthCursor(d);
                setCurrentDate(d);
              }}
              className={`${BTN_BASE} ${BTN_GENERAL}`}
              style={{ padding: "6px 10px" }}
              title="Previous month"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              onClick={() => {
                const d = addMonths(monthCursor, 1);
                setMonthCursor(d);
                setCurrentDate(d);
              }}
              className={`${BTN_BASE} ${BTN_GENERAL}`}
              style={{ padding: "6px 10px" }}
              title="Next month"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => setShowWeek((v) => !v)}
            className="font-bold"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: accent,
            }}
          >
            {showWeek ? <FaChevronDown /> : <FaRight />}
            <span>This Week</span>
          </button>

          {showWeek && (
            <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
              {(() => {
                const start = startOfWeek(currentDate, { weekStartsOn: 1 });
                return eachDayOfInterval({ start, end: addDays(start, 6) }).map((d) => {
                  const isActive = isSameDay(d, currentDate);
                  const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" };
                  return isActive ? (
                    <button key={d.toISOString()} type="button" className={`${BTN_BASE} ${BTN_GENERAL}`} style={{ padding: "6px 10px", ...rowStyle }}>
                      <span>{format(d, "EEE")}</span>
                      <span>{format(d, "d MMM")}</span>
                    </button>
                  ) : (
                    <button
                      key={d.toISOString()}
                      type="button"
                      className={outlineChipClass}
                      style={{ ...rowStyle, padding: "6px 10px", borderColor: accent, color: accent }}
                      onClick={() => handleSelectDay(d)}
                    >
                      <span>{format(d, "EEE")}</span>
                      <span>{format(d, "d MMM")}</span>
                    </button>
                  );
                });
              })()}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => setShowMonths((v) => !v)}
            className="font-bold"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: accent,
            }}
          >
            {showMonths ? <FaChevronDown /> : <FaRight />}
            <span>Months</span>
          </button>

          {showMonths && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginTop: 6 }}>
              {monthNames.map((m, i) => {
                const active = monthCursor.getMonth() === i;
                return active ? (
                  <button key={m} type="button" className={`${BTN_BASE} ${BTN_GENERAL}`} style={{ padding: "6px 0", justifyContent: "center" }}>
                    {m}
                  </button>
                ) : (
                  <button
                    key={m}
                    type="button"
                    className={outlineChipClass}
                    style={{ justifyContent: "center", padding: "6px 0", borderColor: accent, color: accent }}
                    onClick={() => handleSelectMonth(i)}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <hr style={{ border: 0, borderTop: "1px solid #e5e7eb", margin: "12px 0" }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: theme.accent || "var(--accent-color)" }}>
            Unassigned Work ({leftRailUnassigned.length + leftRailUnassignedTasks.length})
          </div>
          <input
            type="text"
            placeholder="Filter by job ref…"
            value={filterUnassigned}
            onChange={(e) => setFilterUnassigned(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb", marginBottom: 8 }}
          />
          <div style={{ display: "grid", gap: 6 }}>
            {leftRailUnassigned.length === 0 && leftRailUnassignedTasks.length === 0 && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>No unassigned work.</div>
            )}
            {leftRailUnassignedTasks.map((t) => {
              const attention = t.execution_state === "awaiting_reassignment";
              const line = t.isTaskList ? `${t.title} (${t.task_count || 0} tasks)` : [t.job_ref || t.parent_job_ref, t.title || t.task_ref || `Task #${t.id}`].filter(Boolean).join(" - ");
              return (
                <div
                  key={`task-${t.id}`}
                  draggable
                  onDragStart={(e) => beginDragUnassigned(t, e)}
                  onDragEnd={endDragUnassigned}
                  title="Drag onto any resource/day cell or time slot to assign this workflow task"
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 10px", borderRadius: 10,
                    border: attention ? "1px dashed #b91c1c" : "1px dashed #94a3b8",
                    background: attention ? "#fef2f2" : "#f8fafc", cursor: "grab", userSelect: "none",
                  }}
                >
                  <span style={{ opacity: 0.6, marginTop: 2 }}>≡</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 900, textTransform: "uppercase", color: attention ? "#b91c1c" : "#475569" }}>
                      {t.isTaskList ? "Task List" : "Workflow Task"}{attention ? " · Awaiting Reassignment" : ""}
                    </div>
                    {t.isTaskList && <div style={{ fontSize: 11, fontWeight: 700 }}>{taskListContextLabel(t)}</div>}
                    <div style={{ fontWeight: 700, whiteSpace: "normal", lineHeight: 1.2 }}>{line}</div>
                    {!t.isTaskList && <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{Number(t.duration_minutes || 60)} min</div>}
                  </div>
                </div>
              );
            })}
            {leftRailUnassigned.map((j) => {
              const typeLabel = jobTypesById[Number(j.job_type_id)]?.short_code || jobTypesById[Number(j.job_type_id)]?.name || "";
              const line = [j.job_ref || `Job #${j.id}`, j.location_name || "", typeLabel].filter(Boolean).join(" - ");
              return (
                <div
                  key={j.id}
                  draggable
                  onDragStart={(e) => beginDragUnassigned(j, e)}
                  onDragEnd={endDragUnassigned}
                  title="Drag onto any resource/day cell or time slot to assign"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: "1px dashed #e5e7eb",
                    background: "#fafafa",
                    cursor: "grab",
                    userSelect: "none",
                  }}
                >
                  <span style={{ opacity: 0.6, marginTop: 2 }}>≡</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, whiteSpace: "normal", lineHeight: 1.2 }}>{line}</div>
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                      {Number(j.planned_duration_minutes || j.duration_minutes || 60)} min
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </aside>

      <main
        style={{
          flex: 1,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          overflow: "hidden",
          fontFamily: theme.fontBody || "var(--font-body)",
        }}
      >
        <ThemedToolbar
          label={toolbarLabel}
          onNavigate={(action) => {
            const computeNextDate = () => {
              if (action === "TODAY") return new Date();
              if (action === "PREV") {
                if (currentView === Views.MONTH || currentView === "month") return addMonths(currentDate, -1);
                if (currentView === Views.DAY || currentView === "day") return addDays(currentDate, -1);
                if (currentView === Views.AGENDA || currentView === "agenda") return addDays(currentDate, -7);
                return addDays(currentDate, -7);
              }
              if (action === "NEXT") {
                if (currentView === Views.MONTH || currentView === "month") return addMonths(currentDate, 1);
                if (currentView === Views.DAY || currentView === "day") return addDays(currentDate, 1);
                if (currentView === Views.AGENDA || currentView === "agenda") return addDays(currentDate, 7);
                return addDays(currentDate, 7);
              }
              return currentDate;
            };
            const next = computeNextDate();
            setCurrentDate(next);
            setMonthCursor(startOfMonth(next));
          }}
          onView={(v) => setCalendarView(v)}
          view={currentView}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        {(currentView === Views.WEEK || currentView === "week") && (
          <section
            aria-label={`Unassigned work for week commencing ${visibleWeekCommencing || ""}`}
            style={{
              borderTop: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
              padding: "8px 10px 10px",
              background: "#fff",
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 7,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 800, color: theme.accent || "var(--accent-color)" }}>
                Unassigned This Week ({weekRailUnassigned.length + weekRailUnassignedTasks.length})
              </div>
              <div style={{ fontSize: 11, opacity: 0.65, whiteSpace: "nowrap" }}>
                W/C {visibleWeekCommencing ? format(toDate(`${visibleWeekCommencing}T12:00:00`), "d MMM yyyy") : ""}
              </div>
            </div>

            {weekRailUnassigned.length === 0 && weekRailUnassignedTasks.length === 0 ? (
              <div style={{ fontSize: 12, color: "#6b7280", padding: "4px 0" }}>No unassigned work for this week.</div>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  overflowX: "auto",
                  overflowY: "hidden",
                  paddingBottom: 2,
                  minWidth: 0,
                }}
              >
                {weekRailUnassignedTasks.map((t) => {
                  const attention = t.execution_state === "awaiting_reassignment";
                  const line = t.isTaskList ? `${t.title} (${t.task_count || 0} tasks)` : [t.job_ref || t.parent_job_ref, t.title || t.task_ref || `Task #${t.id}`]
                    .filter(Boolean)
                    .join(" - ");
                  return (
                    <div
                      key={`week-task-${t.id}`}
                      draggable
                      onDragStart={(e) => beginDragUnassigned(t, e)}
                      onDragEnd={endDragUnassigned}
                      title="Drag onto any resource/day cell or time slot to assign this workflow task"
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: attention ? "1px dashed #b91c1c" : "1px dashed #94a3b8",
                        background: attention ? "#fef2f2" : "#f8fafc",
                        cursor: "grab",
                        userSelect: "none",
                        flex: "0 0 230px",
                        maxWidth: 230,
                      }}
                    >
                      <span style={{ opacity: 0.6, marginTop: 2 }}>≡</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            color: attention ? "#b91c1c" : "#475569",
                          }}
                        >
                          {t.isTaskList ? "Task List" : "Workflow Task"}{attention ? " · Awaiting Reassignment" : ""}
                        </div>
                        {t.isTaskList && <div style={{ fontSize: 11, fontWeight: 700 }}>{taskListContextLabel(t)}</div>}
                    <div style={{ fontWeight: 700, whiteSpace: "normal", lineHeight: 1.2 }}>{line}</div>
                        {!t.isTaskList && <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{Number(t.duration_minutes || 60)} min</div>}
                      </div>
                    </div>
                  );
                })}

                {weekRailUnassigned.map((j) => {
                  const typeLabel =
                    jobTypesById[Number(j.job_type_id)]?.short_code ||
                    jobTypesById[Number(j.job_type_id)]?.name ||
                    "";
                  const line = [j.job_ref || `Job #${j.id}`, j.location_name || "", typeLabel].filter(Boolean).join(" - ");
                  return (
                    <div
                      key={`week-job-${j.id}`}
                      draggable
                      onDragStart={(e) => beginDragUnassigned(j, e)}
                      onDragEnd={endDragUnassigned}
                      title="Drag onto any resource/day cell or time slot to assign"
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: "1px dashed #e5e7eb",
                        background: "#fafafa",
                        cursor: "grab",
                        userSelect: "none",
                        flex: "0 0 230px",
                        maxWidth: 230,
                      }}
                    >
                      <span style={{ opacity: 0.6, marginTop: 2 }}>≡</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, whiteSpace: "normal", lineHeight: 1.2 }}>{line}</div>
                        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                          {Number(j.planned_duration_minutes || j.duration_minutes || 60)} min
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {info && (
          <div style={{ background: "#eef6ff", border: "1px solid #b3d4ff", padding: 10, color: "#084298" }}>{info}</div>
        )}
        {error && (
          <div style={{ background: "#fff", border: "1px solid #ffcccc", padding: 12, color: "#a10000" }}>{error}</div>
        )}

        {currentView === Views.DAY || currentView === "day" ? (
          <DnDCalendar
            localizer={localizer}
            defaultView={Views.DAY}
            view={currentView}
            views={[Views.DAY]}
            onView={(v) => setCalendarView(v)}
            date={currentDate}
            onNavigate={(d) => {
              setCurrentDate(d);
              setMonthCursor(startOfMonth(d));
            }}
            events={[...events, ...taskEvents].map((e) => ({ ...e, title: e.isTask ? e.title : buildTitle(e) }))}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            selectable={true}
            onSelectSlot={onSelectSlot}
            onSelectEvent={onSelectEvent}
            resizable
            onEventDrop={onEventDrop}
            onEventResize={onEventResize}
            dragFromOutsideItem={() => {
              if (!window.__RBC_EXTERNAL_DRAG__) return null;
              const item = window.__CAL_DRAG_TASK__ || window.__CAL_DRAG_JOB__;
              if (!item) return null;
              return { title: item.isTask ? `[Task] ${item.title || item.task_ref}` : `[Assign] ${item.job_ref}`, start: new Date(), end: new Date(), allDay: false };
            }}
            onDropFromOutside={onDropFromOutsideRBC}
            resources={orderedResources}
            resourceIdAccessor="resourceId"
            resourceTitleAccessor="resourceTitle"
            onRangeChange={(range) => {
              fetchAssignments(range);
              fetchUnassigned(range);
              fetchTaskProjection();
            }}
            eventPropGetter={eventPropGetter}
            components={{
              eventWrapper: (wrapProps) => {
                const { event, children } = wrapProps;
                const onCtx = (e) => {
                  e.preventDefault();
                  setMenuState({ open: true, x: e.clientX, y: e.clientY, targetEvent: event });
                };
                return <div onContextMenu={onCtx}>{children}</div>;
              },
            }}
            style={{ height: "calc(100vh - 64px)" }}
            popup
            toolbar={false}
            longPressThreshold={250}
            step={settings.slotMode === "half" ? 30 : 60}
            timeslots={settings.slotMode === "half" ? 2 : 1}
            min={setHours(setMinutes(new Date(), 0), settings.startHour)}
            max={setHours(setMinutes(new Date(), 0), settings.endHour)}
            scrollToTime={setHours(setMinutes(new Date(), 0), Math.max(6, settings.startHour))}
          />
        ) : currentView === Views.AGENDA || currentView === "agenda" ? (
          <Calendar
            localizer={localizer}
            defaultView={Views.AGENDA}
            view={Views.AGENDA}
            views={[Views.AGENDA]}
            date={currentDate}
            onNavigate={(d) => {
              setCurrentDate(d);
              setMonthCursor(startOfMonth(d));
            }}
            events={[...events, ...taskEvents].map((e) => ({ ...e, title: e.isTask ? e.title : buildTitle(e) }))}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            onSelectEvent={onSelectEvent}
            onRangeChange={(range) => {
              fetchAssignments(range);
              fetchUnassigned(range);
              fetchTaskProjection();
            }}
            eventPropGetter={eventPropGetter}
            style={{ height: "calc(100vh - 64px)" }}
            popup
            toolbar={false}
          />
        ) : (
          <ResourceDayMatrix
            titleMode={currentView === Views.MONTH || currentView === "month" ? "month" : "week"}
            days={plannerDays}
            currentDate={currentDate}
            visibleOrderedResources={visibleOrderedResources}
            colours={colours}
            events={[...events, ...taskEvents]}
            settings={settings}
            onCreateAssignment={createPlannerItemForResourceDay}
            onMoveAssignment={moveAssignmentToResourceDay}
            onOpenFlyout={(start, end, rid) => openFlyoutWithRange(start, end, rid)}
            onOpenContextMenu={({ x, y, eventObj }) => setMenuState({ open: true, x, y, targetEvent: eventObj })}
            onReorderResources={(ids) => mergeVisibleReorder(ids)}
            buildTitle={(ev) => (ev.isTask ? ev.title : buildTitle(ev))}
          />
        )}
      </main>

      <ContextMenu
        open={menuState.open}
        x={menuState.x}
        y={menuState.y}
        eventObj={menuState.targetEvent}
        resources={orderedResources}
        onChoose={handleContextChoose}
        onClose={() => setMenuState({ open: false, x: 0, y: 0, targetEvent: null })}
      />

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        resources={resources}
        order={order}
        setOrder={setOrder}
        colours={colours}
        setColours={setColours}
        visibility={visibility}
        setVisibility={setVisibility}
        settings={settings}
        setSettings={setSettings}
      />

      <Toast />
    </div>
  );
}